import React, { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { gruposDoFunil } from '../data/etapas';
import hubService from '../hub/HubConnectionService';
import { useGlobalNotification } from '../contexts/GlobalNotificationContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../services/api';
import {
  LuArrowLeft, LuPlus, LuPaperclip, LuTrash2, LuPencil, LuImage, LuMusic, LuVideo, LuFileText,
  LuSparkles, LuUserPlus, LuDownload, LuEye, LuCheck, LuMegaphone, LuMessageCircle, LuMinus, LuX,
  LuListTodo, LuStickyNote, LuPackage, LuGlobe, LuFilterX,
} from 'react-icons/lu';
import { FaWhatsapp, FaInstagram, FaFacebook, FaFacebookMessenger } from 'react-icons/fa';
import { ProductService, type Product } from '../services/ProductService';
import { ConversaProdutoService, type ConversaProduto } from '../services/ConversaProdutoService';
import { ChatService } from '../services/ChatService';
import PageHeader, { type PageTab } from '../components/PageHeader';
import AbaAssinatura from '../components/AbaAssinatura';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import Select from '../components/Select';
import { TipoContatoService, type TipoContato } from '../services/TipoContatoService';
import Dialog from '../components/Dialog';
import useCelular from '../hooks/useCelular';
import OrganizationPicker from '../components/OrganizationPicker';
import { OrganizationService, type Organization } from '../services/OrganizationService';

/** 45754279000137 → 45.754.279/0001-37; o que não tiver 14 dígitos volta como veio. */
const formatarCnpj = (v: string) => {
  const d = v.replace(/\D/g, '');
  return d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : v;
};
import ObservationEditor from '../components/ObservationEditor';
import ConversationWindow from '../components/ConversationWindow';
import ConversationSkeleton from '../components/ConversationSkeleton';
import MessageInput from '../components/MessageInput';
import { lerJanela, useJanelaAtendimento, type JanelaAtendimento } from '../hooks/useJanelaAtendimento';
import { type Message } from '../data/mockData';
import aviso, { textoDoErro, SEM_SERVIDOR } from '../lib/aviso';
import { OpportunityService, type DetalheConversa, type HandRaise, type MotivoPerdaHistorico, type OrigemMarketing } from '../services/OpportunityService';
import InfoTitulo from '../components/InfoTitulo';
import { rotuloOrigemMarketing } from '../utils/origemMarketing';
import LossReasonModal from '../components/LossReasonModal';
import WinDealProductModal from '../components/WinDealProductModal';
import { ContactService } from '../services/ContactService';
import { AttachmentService } from '../services/AttachmentService';
import { AgendaService } from '../services/AgendaService';
import { TarefaService, TipoTarefaService, type Tarefa, type TipoTarefa, type DesfechoTarefa } from '../services/TarefaService';
import { descreverAcao } from '../components/EscolherTrilhoModal';
import type { ContatoAnexoResponseDTO, TipoAnexo } from '../types/Attachment';
import CampoPersonalizadoValorService, { type CampoPersonalizadoValor, type PreencherValorItem } from '../services/CampoPersonalizadoValorService';
import { TipoCampoPersonalizado } from '../services/CampoPersonalizadoService';
import { getUserPermissions, hasPermission, temFuncionalidade, PERMISSOES, ehAdministradorDaEmpresa } from '../config/permissions';
import '../components/ListKit.css';
import AcoesDoAnuncio from '../components/AcoesDoAnuncio';
import { capaDoAnuncio, campanhaDoAnuncio, nomeDoAnuncio, objetivoDoAnuncio, rotuloRedeDoAnuncio, statusDoAnuncio, textoDoAnuncio } from '../services/TrafegoPagoService';
import { telefoneLegivel } from '../utils/telefone';
import type { AtribuicaoAnuncio } from '../services/TrafegoPagoService';
import RdStationSimbolo from '../components/apps/RdStationSimbolo';
import DocumentosOportunidade from '../components/documentos/DocumentosOportunidade';
import './PipelineBoard.css';
// A conversa (ConversationWindow/MessageInput) usa as classes e os tokens `ch-` do Chat;
// a aba WhatsApp carrega o CSS e veste `.ch-screen` só no seu bloco, para a paleta do
// chat não vazar para o resto da página.
import './ChatScreen.css';
import './DealDetailsPage.css';
import { useRegistroDoAgente } from '../contexts/AgenteContext';
import { ACAO_ASSISTENTE_EVENT } from '../services/AssistenteService';

/**
 * A oportunidade como PÁGINA (fase 2 do redesign de Oportunidades, 2026-09-12).
 *
 * Antes abria como gaveta de 90vw por cima do quadro — na prática uma página
 * inteira, só que sem URL própria e sem voltar. Agora é a rota
 * `/pipeline/deal/:id` (que já existia e a busca da sidebar já usava), no
 * padrão de tela: `PageHeader` com a seta de voltar, etapa e temperatura no
 * cabeçalho, as sete abas na faixa do cabeçalho, ficha à esquerda e o conteúdo
 * da aba à direita. Formulários (tarefa, produto) abrem em `RecordPanel`;
 * confirmações usam o `Dialog`. Prefixo `dd-`.
 */

type DealProduct = ConversaProduto;

/* As etapas da ficha são as do funil da empresa: a lista aqui era fixa em sete, e uma
   oportunidade numa etapa criada depois ficaria sem rótulo no select e fora da régua. */
const etapasDaFicha = () => gruposDoFunil().flatMap(g =>
  g.statusIds.map(id => ({ value: String(id), label: g.title, dot: `var(--pl-st-${g.cor})` })));

const TEMPERATURAS = [
  { value: '0', label: 'Sem temperatura' },
  { value: '1', label: 'Quente', dot: 'var(--pl-t-hot)' },
  { value: '2', label: 'Morno', dot: 'var(--pl-t-warm)' },
  { value: '3', label: 'Frio', dot: 'var(--pl-t-cold)' },
];

const ABAS: PageTab[] = [
  { id: 'agenda', label: 'Tarefas' },
  { id: 'notes', label: 'Anotações' },
  { id: 'chat', label: 'WhatsApp' },
  { id: 'attachments', label: 'Anexos' },
  { id: 'products', label: 'Produtos' },
  // Só com a funcionalidade `modelos` ligada; o filtro é feito no componente.
  { id: 'documents', label: 'Documentos' },
  // Só com a funcionalidade `assinaturas` ligada; o filtro é feito em `abas`.
  { id: 'assinatura', label: 'Assinatura' },
  // A jornada: chegada, primeiras mensagens, etapas, responsáveis, tarefas… (docs/propostas/historico.html, fase 3).
  { id: 'history', label: 'Histórico' },
];

const brl = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const canal = (id?: number): { glifo: React.ReactNode; nome: string } => {
  switch (id) {
    case 1: case 2: return { glifo: <FaWhatsapp size={13} className="pl-wa" />, nome: 'WhatsApp' };
    case 3: return { glifo: <FaInstagram size={13} />, nome: 'Instagram' };
    case 4: return { glifo: <FaFacebookMessenger size={13} />, nome: 'Messenger' };
    case 5: return { glifo: <LuMegaphone size={13} />, nome: 'Lead Ads' };
    case 6: return { glifo: <LuPencil size={13} />, nome: 'Manual' };
    case 7: return { glifo: <RdStationSimbolo size={13} />, nome: 'RD Station' };
    case 8: return { glifo: <LuGlobe size={13} />, nome: 'Site' };
    default: return { glifo: <LuMessageCircle size={13} />, nome: 'Conversa' };
  }
};

/** Nome da aba da conversa: o canal por onde ela acontece; sem canal de mensagem, "Conversa". */
const rotuloAbaConversa = (idOrigem?: number) => {
  switch (idOrigem) {
    case 1: case 2: case undefined: return 'WhatsApp';
    case 3: return 'Instagram';
    case 4: return 'Messenger';
    default: return 'Conversa';
  }
};

/**
 * A porta pela qual a pessoa entrou, como a ficha lê. Disparo respondido tem nome próprio;
 * senão vale o nome que a origem deu à conversão — é o que o cliente cadastrou lá e
 * reconhece aqui ("whatsapp-bold-workplace"), e traduzir isso para um rótulo bonito só
 * funcionaria para os nomes que nós conhecêssemos. Sem nada disso, ela chamou pelo canal.
 */
/** O valor do campo personalizado como se lê: "Sim" em vez de "true", data em dd/mm/aaaa. */
const valorLegivelDoCampo = (field: CampoPersonalizadoValor, valor?: string): string => {
  const v = (valor ?? '').trim();
  if (!v) return '';
  if (field.tipo === TipoCampoPersonalizado.Booleano) return v === 'true' ? 'Sim' : v === 'false' ? 'Não' : v;
  if (field.tipo === TipoCampoPersonalizado.Data && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    const [a, m, d] = v.slice(0, 10).split('-');
    return `${d}/${m}/${a}`;
  }
  return v;
};

/** O anúncio que trouxe a pessoa, na seção Origem da ficha: capa, rede, título, texto e os
    botões da faixa do Chat. Desenho: docs/propostas/pipeline/ficha-e-anuncio.html (16/10/2026).
    Com a conta de anúncios conectada, o nome do anúncio vem da Meta e o título vira texto. */
const CartaoOrigemAnuncio = ({ atribuicao }: { atribuicao: AtribuicaoAnuncio }) => {
  const capa = capaDoAnuncio(atribuicao);
  const campanha = campanhaDoAnuncio(atribuicao);
  const nome = nomeDoAnuncio(atribuicao);
  const texto = textoDoAnuncio(atribuicao);
  const objetivo = objetivoDoAnuncio(atribuicao);
  const status = statusDoAnuncio(atribuicao);
  /* O objetivo vai junto da campanha: é a intenção dela ("Campanha · Outubro · Vendas"). */
  const linhaCampanha = [campanha && `Campanha · ${campanha}`, objetivo && (campanha ? objetivo : `Objetivo · ${objetivo}`)]
    .filter(Boolean).join(' · ');
  return (
    <div className={`dd-ori${capa ? '' : ' is-sem-capa'}`}>
      {capa && <img className="dd-ori-capa" src={capa} alt="" loading="lazy" />}
      <div className="dd-ori-tx">
        <span className="dd-ori-rotulo">
          {atribuicao.anuncioRede === 'facebook' ? <FaFacebook size={11} /> : atribuicao.anuncioRede === 'instagram' ? <FaInstagram size={11} /> : null}
          {rotuloRedeDoAnuncio(atribuicao)}
          {status && <span className={`badge badge-${status.tom} dd-ori-status`}>{status.rotulo}</span>}
        </span>
        <span className="dd-ori-titulo">{nome}</span>
        {texto && <span className="dd-ori-corpo">{texto}</span>}
        {linhaCampanha && <span className="dd-ori-campanha">{linhaCampanha}</span>}
        <div className="dd-ori-acoes"><AcoesDoAnuncio atribuicao={atribuicao} /></div>
      </div>
    </div>
  );
};

const portaDaLevantada = (lm: HandRaise): string => {
  if (lm.idCampanha) return `Resposta ao disparo${lm.nomeCampanha ? `: ${lm.nomeCampanha}` : ''}`;
  if (lm.conversao) return lm.conversao;
  /* "Chamou no" só vale onde se conversa (WhatsApp, Instagram, Messenger). Para site, anúncio
     e RD a pessoa não chamou: ela entrou por ali — e "Chamou no Site" não existe em português. */
  const nome = canal(lm.idCanal).nome;
  return lm.idCanal >= 1 && lm.idCanal <= 4 ? `Chamou no ${nome}` : `Entrou por ${nome}`;
};

/** Só o nome do arquivo: `caminhoAnexo` vem como caminho completo. */
const nomeArquivo = (caminho?: string) => (caminho || '').split(/[\\/]/).pop() || 'anexo';

const iconeAnexo = (tipo: string) =>
  tipo === 'image' ? <LuImage size={14} /> : tipo === 'audio' ? <LuMusic size={14} /> : tipo === 'video' ? <LuVideo size={14} /> : <LuFileText size={14} />;

const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const paraDataInput = (d: Date | null) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '');

interface AttachmentPreviewProps {
  id: number;
  tipo: string;
}

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({ id, tipo }) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (tipo !== 'image') return;
    let active = true;
    AttachmentService.getAttachmentFileUrl(id)
      .then(fetchedUrl => { if (active) setUrl(fetchedUrl); })
      .catch(err => console.error('Error fetching preview:', err));
    return () => { active = false; };
  }, [id, tipo]);

  if (tipo === 'image' && url) return <img src={url} alt="" className="dd-file-thumb" />;
  return <span className="dd-file-ico">{iconeAnexo(tipo)}</span>;
};

interface Deal {
  id: number;
  title: string;
  value: string;
  tag: 'hot' | 'warm' | 'cold' | 'new';
  owner: string;
  date: string;
  contactId?: number;
  statusId?: number;
  rawValue?: number;
  phone?: string;
  details?: DetalheConversa[];
  dataFechamentoVenda?: string | null;
  campanha?: boolean;
  idCampanha?: number | null;
  nomeCampanha?: string;
  /* Anúncio é outro eixo: `campanha`/`idCampanha` acima são DISPARO de WhatsApp.
     Uma conversa pode ter os dois, e o rótulo na tela precisa dizer qual é qual. */
  atribuicaoAnuncio?: AtribuicaoAnuncio | null;
  tipoLeadId?: number;
  ownerId?: number;
  idOrigemOportunidade?: number;
  descricaoOrigemOportunidade?: string;
  /* De ONDE veio, quando a porta soube dizer. O de cima é o CANAL, onde se conversa. */
  origemMarketing?: OrigemMarketing | null;
  organizacaoId?: number | null;
  organizacaoNome?: string;
  /** Contato fora do funil: sem etapa, sem valor e sem desfecho; a conversa só vive no Chat. */
  foraDoFunil?: boolean;
  tipoContatoId?: number | null;
}

interface ApiMessage {
  idMensagem: number;
  idConversa: number;
  mensagem: string;
  origemMensagem: number;
  dataEnvio: string;
  tipoMensagem?: 'text' | 'image' | 'audio' | 'file' | 'document';
  urlMidia?: string;
  caminhoArquivo?: string;
  statusMensagem?: string;
  apagadaEm?: string | null;
}

const apiUrl = import.meta.env.VITE_API_URL;

interface DealDetailsPageProps {
  /** Mantido por compatibilidade; a tela agora vive na rota `/pipeline/deal/:id`. */
  dealId?: string;
}

const DD_PRODUTOS = '90px minmax(0,2fr) minmax(0,.8fr) 100px 110px 110px 56px';

const DealDetailsPage: React.FC<DealDetailsPageProps> = ({ dealId: propDealId }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = propDealId || paramId;
  const navigate = useNavigate();
  const celular = useCelular();

  const permissions = getUserPermissions();

  const [deal, setDeal] = useState<Deal | null>(null);
  const [isLoadingDeal, setIsLoadingDeal] = useState(true);

  const [chatError, setChatError] = useState<string | null>(null);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [messagesByDate, setMessagesByDate] = useState<{ [date: string]: Message[] }>({});
  // Janela de 24h da Meta. `null` enquanto carrega: não trava.
  const [janela, setJanela] = useState<JanelaAtendimento | null>(null);
  const janelaFechada = useJanelaAtendimento(janela);
  /* A aba pode vir no endereço (`?aba=notes`): é assim que o menu do botão direito da Pipeline manda
     abrir a ficha já no lugar certo, e é assim que um link colado sobrevive a recarregar a página —
     o mesmo expediente do `?editar=1` das listagens. Valor desconhecido cai na aba padrão. */
  const [parametros] = useSearchParams();
  const abaPedida = parametros.get('aba');
  /* `dados` só existe no celular: é a ficha, que no computador mora na coluna da esquerda. */
  const [activeTab, setActiveTab] = useState<'dados' | 'notes' | 'chat' | 'attachments' | 'agenda' | 'products' | 'documents' | 'assinatura' | 'history'>(
    // Documentos fica de fora: depende da funcionalidade `modelos`, e um link não pode abrir aba que a tira esconde.
    () => (ABAS.some(a => a.id === abaPedida && a.id !== 'documents') ? abaPedida as 'notes' : 'agenda'),
  );
  /* Documentos emitidos a partir dos modelos (05/10/2026). A aba some quando a
     funcionalidade está desligada para a empresa; a API também recusa a emissão. */
  const abas = useMemo(() => {
    let visiveis = temFuncionalidade('modelos') ? ABAS : ABAS.filter(a => a.id !== 'documents');
    /* Assinaturas é cadastro de quem vende recorrência: empresa sem a
       funcionalidade não ganha aba nova na ficha do negócio. */
    if (!temFuncionalidade('assinaturas')) visiveis = visiveis.filter(a => a.id !== 'assinatura');
    const comRotulo = visiveis.map(a => a.id === 'chat' ? { ...a, label: rotuloAbaConversa(deal?.idOrigemOportunidade) } : a);
    return celular ? [{ id: 'dados', label: 'Dados' }, ...comRotulo] : comRotulo;
  }, [deal?.idOrigemOportunidade, celular]);
  // Voltou para o computador com a aba Dados aberta: lá a ficha já está na coluna da esquerda.
  useEffect(() => {
    if (!celular && activeTab === 'dados') setActiveTab('agenda');
    // No celular a conversa tem a altura da tela: abrir a aba leva o painel ao topo, com o campo de escrever à vista.
    if (celular && activeTab === 'chat') document.querySelector('.dd-pane')?.scrollIntoView({ block: 'start' });
  }, [celular, activeTab]);
  const { isHubConnected } = useGlobalNotification();
  const [emitirDocAberto, setEmitirDocAberto] = useState(false);

  const [handRaises, setHandRaises] = useState<HandRaise[]>([]);
  const [verTodasLevantadas, setVerTodasLevantadas] = useState(false);
  const [isLoadingHandRaises, setIsLoadingHandRaises] = useState(true);
  const [dealProducts, setDealProducts] = useState<DealProduct[]>([]);
  const [isLoadingDealProducts, setIsLoadingDealProducts] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [productQuantity, setProductQuantity] = useState<number>(1);
  const [productCustomPrice, setProductCustomPrice] = useState<string>('');
  const [productSearchQuery, setProductSearchQuery] = useState<string>('');

  const [contactEmail, setContactEmail] = useState<string>('');
  const [organizacao, setOrganizacao] = useState<Organization | null>(null);
  const [contactName, setContactName] = useState<string>('');
  const [contactPhone, setContactPhone] = useState<string>('');

  const [isEditingValue, setIsEditingValue] = useState(false);
  const [editValueInput, setEditValueInput] = useState<string>('');

  const formatCurrency = (value: string | number) => {
    if (value === '' || value === undefined || value === null) return '';
    if (typeof value === 'number') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    }
    const onlyDigits = String(value).replace(/\D/g, '');
    if (onlyDigits === '') return '';
    const numberValue = Number(onlyDigits) / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numberValue);
  };

  const handleValueInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formatted = formatCurrency(rawValue);
    setEditValueInput(formatted);
  };

  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editEmailInput, setEditEmailInput] = useState<string>('');

  const [statusId, setStatusId] = useState(1);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [tipoLeadId, setTipoLeadId] = useState<number | undefined>(undefined);
  const [isUpdatingLeadType, setIsUpdatingLeadType] = useState(false);

  // States for handling temperature validation on status change

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isAssuming, setIsAssuming] = useState(false);
  const [confirmandoTomar, setConfirmandoTomar] = useState(false);
  const [isSavingOrg, setIsSavingOrg] = useState(false);
  // Anotações com editor e anexos (2026-09-13)
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [noteToDelete, setNoteToDelete] = useState<{ id: number; content: string } | null>(null);
  /** Imagens coladas no corpo de uma anotação NOVA: já subiram como anexo da conversa, faltando o vínculo. */
  const [pendingImageIds, setPendingImageIds] = useState<number[]>([]);
  const [isDeletingNote, setIsDeletingNote] = useState(false);

  const [currentUser, setCurrentUser] = useState<{ id: number, nome: string } | null>(null);
  const [campaignInfo, setCampaignInfo] = useState<{ campanha: boolean; idCampanha: number | null; nomeCampanha: string } | null>(null);

  const [attachments, setAttachments] = useState<ContatoAnexoResponseDTO[]>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [appointments, setAppointments] = useState<any[]>([]);
  const [isLoadingAgenda, setIsLoadingAgenda] = useState(false);

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [modalDescricao, setModalDescricao] = useState('');
  const [modalTipoTarefaId, setModalTipoTarefaId] = useState<number | null>(null);
  const [modalDate, setModalDate] = useState<Date | null>(null);
  const [modalTime, setModalTime] = useState<string>('09:00');
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [editingTask, setEditingTask] = useState<Tarefa | null>(null);

  const [observations, setObservations] = useState<any[]>([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  /* `?nova=1` na aba de Anotações abre o formulário junto: "Adicionar anotação" é um passo, não três. */
  const [showAddDetails, setShowAddDetails] = useState(() => abaPedida === 'notes' && parametros.get('nova') === '1');
  const [detailsDescription, setDetailsDescription] = useState('');
  const [detailsStatusId, setDetailsStatusId] = useState(1);
  const [detailsValue, setDetailsValue] = useState(0);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [tasks, setTasks] = useState<Tarefa[]>([]);
  // Próximo passo (tarefa padrão): o desfecho escolhido no Select e o que está sendo registrado.
  const [desfechoEscolhido, setDesfechoEscolhido] = useState<string>('');
  const [registrandoDesfecho, setRegistrandoDesfecho] = useState(false);
  /* Um desfecho que precisa de um passo antes (temperatura, produto, motivo) fica aqui
     enquanto o passo está aberto; os modais de etapa já existentes chamam de volta. */
  const [desfechoPendente, setDesfechoPendente] = useState<{ tarefa: Tarefa; desfecho: DesfechoTarefa } | null>(null);
  const [usuariosEmpresa, setUsuariosEmpresa] = useState<{ id: number; nome: string }[]>([]);
  const [modalResponsavelId, setModalResponsavelId] = useState<number | null>(null);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [tiposTarefa, setTiposTarefa] = useState<TipoTarefa[]>([]);

  const [customFields, setCustomFields] = useState<CampoPersonalizadoValor[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<number, string>>({});
  const [isLoadingCustomFields, setIsLoadingCustomFields] = useState(false);
  const [isSavingCustomFields, setIsSavingCustomFields] = useState(false);
  /** O campo personalizado aberto no lápis — um por vez, como Valor e E-mail. */
  const [campoEmEdicao, setCampoEmEdicao] = useState<number | null>(null);

  const [lossReasonHistory, setLossReasonHistory] = useState<MotivoPerdaHistorico[]>([]);
  // Perdido e Ganho têm pré-requisito na API (motivo / produto). Em vez de uma
  // aba só para isso, o passo aparece na hora em que a etapa muda (ele, 13/09).
  const [isLossModalOpen, setIsLossModalOpen] = useState(false);
  const [isWinProductModalOpen, setIsWinProductModalOpen] = useState(false);

  // Load Current User
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await apiFetch(`${apiUrl}/api/Auth/me`);
        if (!response.ok) return;
        const result = await response.json();
        if (result.sucesso && result.dados) {
          const userId = result.dados.id || result.dados.userId || 0;
          setCurrentUser({ id: userId, nome: result.dados.userName || result.dados.nome });
        } else if (result.id) {
          const userId = Number(result.id) || 0;
          setCurrentUser({ id: userId, nome: result.userName || result.nome });
        }
      } catch (err) {
        console.error("Erro ao buscar usuário logado:", err);
      }
    };
    fetchCurrentUser();
  }, []);

  /* As levantadas de mão da pessoa (docs/propostas/pipeline/levantadas-de-mao.html).
     Chamada à parte, como produtos e campos personalizados: a lista é do CONTATO e
     atravessa negócios, então não cabe no DTO da oportunidade, que a Pipeline carrega
     para cada card. */
  const fetchHandRaises = useCallback(async () => {
    if (!id) return;
    setIsLoadingHandRaises(true);
    try {
      setHandRaises(await OpportunityService.getHandRaises(Number(id)));
    } finally {
      setIsLoadingHandRaises(false);
    }
  }, [id]);

  useEffect(() => {
    fetchHandRaises();
  }, [fetchHandRaises]);

  const fetchDealProducts = useCallback(async () => {
    if (!id) return;
    setIsLoadingDealProducts(true);
    try {
      const res = await ConversaProdutoService.listar(Number(id));
      if (res.sucesso) setDealProducts(res.dados ?? []);
    } catch (err) {
      console.error('Erro ao buscar produtos da conversa:', err);
    } finally {
      setIsLoadingDealProducts(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDealProducts();
  }, [fetchDealProducts]);

  // Load available products from API (used in the add-product modal)
  useEffect(() => {
    const fetchAvailableProducts = async () => {
      try {
        setIsLoadingProducts(true);
        const res = await ProductService.getAll();
        if (res.sucesso && res.dados) {
          setAvailableProducts(res.dados);
        }
      } catch (err) {
        console.error("Erro ao buscar produtos cadastrados:", err);
      } finally {
        setIsLoadingProducts(false);
      }
    };
    fetchAvailableProducts();
  }, []);

  // Fetch Deal details
  const fetchDealInfo = useCallback(async (silent = false) => {
    if (!silent) setIsLoadingDeal(true);
    try {
      const currentOpp = await OpportunityService.getOpportunityById(Number(id));

      if (currentOpp) {
        const mappedDeal: Deal = {
          id: currentOpp.idConversa,
          title: currentOpp.nomeContato || currentOpp.nomeOrganizacao || currentOpp.numeroWhatsapp,
          value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(currentOpp.valor || 0),
          rawValue: currentOpp.valor || 0,
          tag: 'new',
          owner: currentOpp.nomeUsuarioResponsavel || 'Sistema',
          date: new Date(currentOpp.dataConversaCriada).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
          contactId: currentOpp.idContato,
          statusId: currentOpp.idStauts,
          phone: currentOpp.numeroWhatsapp,
          details: currentOpp.detalhesConversa,
          dataFechamentoVenda: currentOpp.dataFechamentoVenda,
          campanha: currentOpp.campanha,
          idCampanha: currentOpp.idCampanha,
          nomeCampanha: currentOpp.nomeCampanha,
          atribuicaoAnuncio: currentOpp.atribuicaoAnuncio ?? null,
          tipoLeadId: currentOpp.tipoLeadId,
          ownerId: currentOpp.idUsuarioResponsavel,
          idOrigemOportunidade: currentOpp.idOrigemOportunidade,
          descricaoOrigemOportunidade: currentOpp.descricaoOrigemOportunidade,
          origemMarketing: currentOpp.origemMarketing ?? null,
          organizacaoId: currentOpp.idOrganizacao ?? null,
          organizacaoNome: currentOpp.nomeOrganizacao || undefined,
          foraDoFunil: currentOpp.foraDoFunil === true,
          tipoContatoId: currentOpp.tipoContatoId ?? null,
        };
        setDeal(mappedDeal);
        setStatusId(currentOpp.idStauts);
        setDetailsStatusId(currentOpp.idStauts);
        setDetailsValue(currentOpp.valor || 0);
        
        const resolvedTipoLeadId = mappedDeal.tipoLeadId !== undefined ? mappedDeal.tipoLeadId : 0;
        setTipoLeadId(resolvedTipoLeadId);
      } else {
        aviso.erro("Oportunidade não encontrada.");
        /* Dentro do painel de outra tela (Tarefas), redirecionar tiraria a
           pessoa da lista em que ela estava; o painel fica vazio e ela fecha. */
        if (!propDealId) navigate('/pipeline');
      }
    } catch (e) {
      aviso.erro("Não foi possível carregar os detalhes da oportunidade.");
    } finally {
      setIsLoadingDeal(false);
    }
  }, [id, navigate, propDealId]);

  useEffect(() => {
    fetchDealInfo();
  }, [fetchDealInfo]);

  const handleAssumeDeal = async () => {
    if (!deal || !currentUser || isAssuming) return;

    setIsAssuming(true);
    const toastId = aviso.carregando('Assumindo conversa…');
    try {
      const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${deal.id}/TranferirConversa`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          IdResponsavelAtual: deal.ownerId || 0,
          IdNovoResponsavel: currentUser.id
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.mensagem || 'Falha ao assumir conversa.');
      }
      aviso.ok('Conversa assumida.', { id: toastId });
      fetchDealInfo(true);
    } catch (err: any) {
      aviso.erro(textoDoErro(err, 'Não foi possível assumir a conversa.'), { id: toastId });
    } finally {
      setIsAssuming(false);
      setConfirmandoTomar(false);
    }
  };

  // Messages fetcher
  /* `silencioso`: recarga por mensagem que chegou ao vivo, sem trocar a conversa pelo esqueleto. */
  const fetchMessages = useCallback(async (silencioso = false) => {
    if (!deal) return;
    if (!silencioso) setIsLoadingChat(true);
    setChatError(null);
    try {
      const response = await fetch(`${apiUrl}/api/Chat/Conversas/${deal.id}/Mensagens`, {
        credentials: 'include'
      });
      if (!response.ok) {
        if (response.status === 404) {
          setMessagesByDate({});
          return;
        }
        throw new Error('Falha ao carregar mensagens.');
      }
      const result = await response.json();
      setJanela(lerJanela(result.dados));
      if (result.sucesso && Array.isArray(result.dados.mensagens)) {
        const mapApiStatus = (statusMensagem?: string): Message['status'] => {
          switch (statusMensagem) {
            case 'entregue':
            case 'delivered': return 'delivered';
            case 'lida':
            case 'read': return 'read';
            case 'enviada':
            case 'sent': return 'sent';
            default: return 'sent';
          }
        };

        const grouped = result.dados.mensagens.reduce((acc: { [date: string]: Message[] }, msg: ApiMessage) => {
          const date = new Date(msg.dataEnvio).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const formattedMessage: Message = {
            id: msg.idMensagem,
            text: msg.mensagem,
            sender: msg.origemMensagem === 0 ? 'other' : (msg.origemMensagem === 1 ? 'me' : 'ia'),
            time: new Date(msg.dataEnvio).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
            status: mapApiStatus(msg.statusMensagem),
            type: msg.tipoMensagem || 'text',
            mediaUrl: msg.urlMidia || msg.caminhoArquivo,
            apagadaEm: msg.apagadaEm ?? null
          };
          if (!acc[date]) acc[date] = [];
          acc[date].push(formattedMessage);
          return acc;
        }, {});
        setMessagesByDate(grouped);
      } else {
        setMessagesByDate({});
      }
    } catch (error: any) {
      setChatError("Não foi possível carregar a conversa.");
    } finally {
      setIsLoadingChat(false);
    }
  }, [deal]);

  // Observations fetcher
  const fetchObservations = useCallback(async () => {
    if (!deal) return;
    if (deal.details && deal.details.length > 0) {
      const mappedDetails = deal.details.map(d => ({
        id: d.idDetalhe || Math.random(),
        content: d.descricaoDetalhe,
        userId: d.idUsuarioCriador,
        userName: d.nomeUsuarioCriador,
        createdAt: d.dataDetalheCriado
      }));
      setObservations(mappedDetails);
      return;
    }
    setIsLoadingNotes(true);
    setNotesError(null);
    try {
      const response = await fetch(`${apiUrl}/api/Detalhes/PorConversa/${deal.id}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Falha ao carregar o histórico.');
      const result = await response.json();
      if (result.sucesso) {
        setObservations(result.dados);
      } else {
        setObservations([]);
      }
    } catch (err: any) {
      setNotesError("Não há anotações para esta conversa.");
    } finally {
      setIsLoadingNotes(false);
    }
  }, [deal]);

  // Attachments fetcher
  const fetchAttachments = useCallback(async () => {
    if (!deal) return;
    setIsLoadingAttachments(true);
    setAttachmentError(null);
    try {
      const data = await AttachmentService.getAttachmentsByConversation(deal.id);
      setAttachments(data);
    } catch (err: any) {
      setAttachmentError(err.message);
    } finally {
      setIsLoadingAttachments(false);
    }
  }, [deal]);

  // Agenda fetcher
  const fetchAppointments = useCallback(async () => {
    if (!deal) return;
    setIsLoadingAgenda(true);
    try {
      const data = await AgendaService.getEventsByConversation(deal.id);
      setAppointments(data);
    } catch (err) {
      console.error("Error fetching agenda:", err);
    } finally {
      setIsLoadingAgenda(false);
    }
  }, [deal]);

  // Custom Fields fetcher
  const fetchCustomFields = useCallback(async () => {
    if (!deal) return;
    setIsLoadingCustomFields(true);
    try {
      const data = await CampoPersonalizadoValorService.buscarPorConversa(deal.id);
      setCustomFields(data);
      const initialValues: Record<number, string> = {};
      data.forEach(field => {
        initialValues[field.campoPersonalizadoId] = field.valor ?? '';
      });
      setCustomFieldValues(initialValues);
      setCampoEmEdicao(null);
    } catch (err: any) {
      aviso.erro(textoDoErro(err, 'Não foi possível carregar os campos personalizados.'));
    } finally {
      setIsLoadingCustomFields(false);
    }
  }, [deal]);

  // Loss Reason fetcher
  const fetchLossHistory = useCallback(async () => {
    if (!deal) return;
    try {
      setLossReasonHistory(await OpportunityService.getMotivosPerdaHistorico(deal.id));
    } catch (err) {
      console.error('Erro ao buscar motivo de perda:', err);
    }
  }, [deal]);

  const patchStatus = async (idStatus: number, extra: Record<string, unknown> = {}) => {
    if (!deal) throw new Error('Oportunidade não carregada.');
    const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${deal.id}/AtualizarStatus`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idStatus, valor: deal.rawValue || 0, ...extra }),
    });
    const result = await response.json();
    if (!response.ok || !result.sucesso) throw new Error(result.mensagem || 'Falha ao atualizar status.');
  };

  const handleLossReasonConfirm = async (motivoPerdaId: number, motivoPerdaDetalhe: string) => {
    if (!deal) return;
    if (desfechoPendente) {
      // Veio do próximo passo: o desfecho grava a perda, com o motivo, e a API move a etapa.
      setIsLossModalOpen(false);
      const { tarefa, desfecho } = desfechoPendente;
      await registrarDesfecho(tarefa, desfecho, { motivoPerdaId, motivoPerdaDetalhe: motivoPerdaDetalhe || undefined });
      return;
    }
    const toastId = aviso.carregando('Registrando venda perdida…');
    try {
      await patchStatus(6, { motivoPerdaId, motivoPerdaDetalhe: motivoPerdaDetalhe || null });
      aviso.ok('Venda marcada como perdida.', { id: toastId });
      setIsLossModalOpen(false);
      setStatusId(6);
      await fetchDealInfo(true);
      await fetchLossHistory();
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível registrar o motivo da perda.'), { id: toastId });
    }
  };

  const handleLossReasonCancel = () => {
    setIsLossModalOpen(false);
    setDesfechoPendente(null);
    setStatusId(deal?.statusId || 1);
  };

  const handleWinProductConfirm = async () => {
    if (!deal) return;
    if (desfechoPendente) {
      // Produto vinculado pelo modal; agora o desfecho "Ganho" passa na regra da API.
      setIsWinProductModalOpen(false);
      const { tarefa, desfecho } = desfechoPendente;
      await registrarDesfecho(tarefa, desfecho);
      return;
    }
    const toastId = aviso.carregando('Fechando venda…');
    try {
      await patchStatus(3);
      aviso.ok('Venda fechada.', { id: toastId });
      setStatusId(3);
      fetchDealInfo(true);
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível fechar a venda.'), { id: toastId });
      setStatusId(deal.statusId || 1);
    } finally {
      setIsWinProductModalOpen(false);
    }
  };

  const handleWinProductCancel = () => {
    setIsWinProductModalOpen(false);
    setDesfechoPendente(null);
    setStatusId(deal?.statusId || 1);
  };

  const handleCustomFieldChange = (campoPersonalizadoId: number, value: string) => {
    setCustomFieldValues(prev => ({ ...prev, [campoPersonalizadoId]: value }));
  };

  /** Desfaz o que foi digitado no campo aberto e fecha o lápis. */
  const cancelarCampo = (campoId: number) => {
    const original = customFields.find(f => f.campoPersonalizadoId === campoId)?.valor ?? '';
    setCustomFieldValues(prev => ({ ...prev, [campoId]: original }));
    setCampoEmEdicao(null);
  };

  /* Grava SÓ o campo aberto no lápis: a API valida e grava apenas o que recebe. Mandar a
     lista inteira fazia um campo obrigatório ainda vazio barrar a gravação de qualquer outro
     (medido na sonda de 16/10/2026), e punha no histórico campos que ninguém tocou. */
  const handleSaveCustomFields = async (campoId: number) => {
    if (!deal) return;
    setIsSavingCustomFields(true);
    try {
      const digitado = customFieldValues[campoId];
      const valores: PreencherValorItem[] = [{
        campoPersonalizadoId: campoId,
        valor: digitado?.trim() ? digitado : null,
      }];
      await CampoPersonalizadoValorService.preencherValores(deal.id, valores);
      aviso.ok('Informação salva.');
      setCampoEmEdicao(null);
      fetchCustomFields();
    } catch (err: any) {
      aviso.erro(textoDoErro(err, 'Não foi possível salvar os campos personalizados.'));
    } finally {
      setIsSavingCustomFields(false);
    }
  };

  // Send Message
  const handleSendMessage = async (text: string) => {
    if (!text.trim() || !deal || !deal.contactId) return;

    const today = new Date();
    const dateKey = today.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const tempId = Date.now();
    const newMessage: Message = {
      id: tempId,
      text: text,
      sender: 'me',
      time: today.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      status: 'sending'
    };

    setMessagesByDate(prev => {
      const newState = { ...prev };
      const msgs = newState[dateKey] ? [...newState[dateKey]] : [];
      msgs.push(newMessage);
      newState[dateKey] = msgs;
      return newState;
    });

    try {
      const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${deal.id}/EnviarMensagem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(text),
      });
      if (!response.ok) {
        // A API diz o motivo (ex.: janela de 24h fechada).
        const erro = await response.json().catch(() => null);
        throw new Error(erro?.mensagem || 'Erro ao enviar mensagem.');
      }
      fetchMessages(true);
    } catch (error) {
      aviso.erro(textoDoErro(error, 'Não foi possível enviar a mensagem.'));
    }
  };

  // Status Update
  // Mesma leitura que o quadro faz da mensagem da API ("...vincular pelo menos um produto...").
  const ehErroDeProduto = (mensagem?: string) => !!mensagem?.toLowerCase().includes('produto');

  const handleStatusChange = async (newStatus: string) => {
    if (!deal) return;
    const newStatusId = parseInt(newStatus);
    if (newStatusId === statusId) return;

    // Perdido exige motivo na API: o passo aparece aqui, não numa aba à parte.
    if (newStatusId === 6) {
      setIsLossModalOpen(true);
      return;
    }

    // Check if status is 3 (Ganho) and temperature is not set to 1 (Quente)
    if (newStatusId === 3 && tipoLeadId !== 1) {
      setStatusId(3);
      setIsUpdatingStatus(true);
      try {
        await ChatService.atualizarTipoLead(deal.id, 1);
        setTipoLeadId(1);
        
        const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${deal.id}/AtualizarStatus`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idStatus: 3, valor: deal.rawValue || 0 }),
        });
        const result = await response.json();
        if (!response.ok || !result.sucesso) {
          throw new Error(result.mensagem || 'Falha ao atualizar status.');
        }
        aviso.ok('Oportunidade ganha e qualificada como quente.');
        fetchDealInfo(true);
      } catch (error: any) {
        if (ehErroDeProduto(error.message)) { setIsWinProductModalOpen(true); return; }
        aviso.erro(textoDoErro(error, 'Não foi possível atualizar o status.'));
        setStatusId(deal.statusId || 1);
        setTipoLeadId(deal.tipoLeadId || 0);
      } finally {
        setIsUpdatingStatus(false);
      }
      return;
    }

    setStatusId(newStatusId);
    setIsUpdatingStatus(true);
    try {
      const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${deal.id}/AtualizarStatus`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idStatus: newStatusId, valor: deal.rawValue || 0 }),
      });
      const result = await response.json();
      if (!response.ok || !result.sucesso) {
        throw new Error(result.mensagem || 'Falha ao atualizar status.');
      }
      aviso.ok('Status atualizado.');
      fetchDealInfo(true);
    } catch (error: any) {
      if (newStatusId === 3 && ehErroDeProduto(error.message)) { setIsWinProductModalOpen(true); return; }
      aviso.erro(textoDoErro(error, 'Não foi possível atualizar o status.'));
      setStatusId(deal.statusId || 1);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  /* Alternativa a excluir: fornecedor ou contato pessoal não é negócio. Tira o contato do funil e
     fica na conversa (a faixa "Fora do funil" aparece em seguida). Também serve para o botão
     "Levar para o funil" da faixa, com `fora` falso. */
  const [mudandoFunil, setMudandoFunil] = useState(false);
  /* Tirar do funil pergunta antes, como excluir — sai do quadro e das metricas. */
  const [confirmandoForaDoFunil, setConfirmandoForaDoFunil] = useState(false);
  const alterarFunil = async (fora: boolean) => {
    if (!deal?.contactId) return;
    setMudandoFunil(true);
    try {
      await ContactService.definirFunil(deal.contactId, fora);
      aviso.ok(fora ? 'Contato fora do funil.' : 'Contato de volta ao funil.');
      setIsDeleteConfirmOpen(false);
      await fetchDealInfo(true);
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível alterar o funil do contato.'));
    } finally {
      setMudandoFunil(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!deal) return;
    setIsDeleting(true);
    try {
      const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${deal.id}`, {
        method: 'DELETE',
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(result?.mensagem || 'Falha ao excluir conversa.');
      }
      aviso.ok('Conversa excluída.');
      setIsDeleteConfirmOpen(false);
      navigate('/pipeline');
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível excluir a conversa.'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Anotações: texto vem do editor (HTML). Vazio = só tags sem texto.
  const textoDaAnotacao = (html: string) => html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

  const tipoDoArquivo = (file: File): TipoAnexo => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('audio/')) return 'audio';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type === 'application/pdf' || file.type.includes('msword') || file.type.includes('officedocument')) return 'documento';
    return 'outros';
  };

  const anexarArquivosNaAnotacao = async (idDetalhe: number, arquivos: File[]) => {
    if (!deal || arquivos.length === 0) return;
    let falhas = 0;
    for (const arquivo of arquivos) {
      try {
        await AttachmentService.addAttachmentByConversation(deal.id, arquivo, tipoDoArquivo(arquivo), idDetalhe);
      } catch { falhas += 1; }
    }
    if (falhas > 0) aviso.erro(falhas === 1 ? 'Um arquivo não pôde ser anexado.' : `${falhas} arquivos não puderam ser anexados.`);
    fetchAttachments();
  };

  const fecharFormularioAnotacao = (descartar = false) => {
    if (descartar && !editingNoteId && pendingImageIds.length > 0) {
      // Anotação nova cancelada: as imagens coladas subiram só para ela.
      pendingImageIds.forEach(id => AttachmentService.removeAttachment(id).catch(() => { }));
      fetchAttachments();
    }
    setShowAddDetails(false);
    setEditingNoteId(null);
    setDetailsDescription('');
    setPendingFiles([]);
    setPendingImageIds([]);
  };

  /**
   * Imagem colada no corpo: sobe como anexo da anotação (ou da conversa, se a
   * anotação ainda não existe) e volta a URL do arquivo para o `src`. O mesmo
   * arquivo aparece nos anexos da anotação — é de propósito: um lugar só.
   */
  const enviarImagemDaAnotacao = async (arquivo: File): Promise<string | null> => {
    if (!deal) return null;
    try {
      const r = await AttachmentService.addAttachmentByConversation(deal.id, arquivo, 'image', editingNoteId ?? null);
      const id = r?.dados?.id;
      if (!id) throw new Error('A API não devolveu o anexo.');
      if (!editingNoteId) setPendingImageIds(prev => [...prev, id]);
      fetchAttachments();
      return `${apiUrl}/api/Chat/Anexos/${id}/Arquivo`;
    } catch (e: any) {
      aviso.erro(textoDoErro(e, 'Não foi possível enviar a imagem.'));
      return null;
    }
  };

  const abrirEdicaoAnotacao = (obs: { id: number; content: string }) => {
    setEditingNoteId(obs.id);
    setDetailsDescription(obs.content);
    setPendingFiles([]);
    setPendingImageIds([]);
    setShowAddDetails(false);
  };

  // Notes Saver — cria (ou edita) e depois prende os arquivos pendentes à anotação.
  const handleSaveDetails = async () => {
    if (!deal) return;
    const temImagem = /<img\s/i.test(detailsDescription);
    if (!textoDaAnotacao(detailsDescription) && !temImagem && pendingFiles.length === 0) {
      aviso.atencao('Escreva a anotação ou anexe um arquivo.');
      return;
    }
    const conteudo = textoDaAnotacao(detailsDescription) || temImagem ? detailsDescription : '<p>Arquivos anexados.</p>';

    setIsSavingDetails(true);
    try {
      if (editingNoteId) {
        await OpportunityService.updateDetail(editingNoteId, conteudo);
        await anexarArquivosNaAnotacao(editingNoteId, pendingFiles);
        aviso.ok('Anotação atualizada.');
      } else {
        const result = await OpportunityService.addDetails({
          idConversa: deal.id,
          descricao: conteudo,
          statusNegociacaoId: detailsStatusId,
          valor: detailsValue,
        });
        if (!result.sucesso) throw new Error(result.mensagem || 'Erro ao salvar a anotação.');
        if (result.dados) {
          await OpportunityService.linkAttachmentsToDetail(result.dados, pendingImageIds).catch(e => aviso.erro(textoDoErro(e, 'Não foi possível vincular as imagens à anotação.')));
          await anexarArquivosNaAnotacao(result.dados, pendingFiles);
        }
        else if (pendingFiles.length > 0) aviso.atencao('A anotação foi salva, mas os anexos ficaram de fora.');
        aviso.ok('Anotação adicionada.');
      }
      fecharFormularioAnotacao();
      fetchDealInfo(true);
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível salvar a anotação.'));
    } finally {
      setIsSavingDetails(false);
    }
  };

  const excluirAnotacao = async () => {
    if (!noteToDelete) return;
    setIsDeletingNote(true);
    try {
      await OpportunityService.deleteDetail(noteToDelete.id);
      aviso.ok('Anotação excluída.');
      setNoteToDelete(null);
      fetchDealInfo(true);
      fetchAttachments();
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível excluir a anotação.'));
    } finally {
      setIsDeletingNote(false);
    }
  };

  // Attachments Upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!deal) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      let tipo: TipoAnexo = 'outros';
      if (file.type.startsWith('image/')) tipo = 'image';
      else if (file.type.startsWith('audio/')) tipo = 'audio';
      else if (file.type.startsWith('video/')) tipo = 'video';
      else if (file.type === 'application/pdf' || file.type.includes('msword') || file.type.includes('officedocument')) tipo = 'documento';

      await AttachmentService.addAttachmentByConversation(deal.id, file, tipo);
      aviso.ok('Arquivo enviado.');
      fetchAttachments();
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível enviar o arquivo.'));
    } finally {
      setIsUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleDownloadAttachment = async (id: number, filename?: string) => {
    try {
      const url = await AttachmentService.getAttachmentFileUrl(id);
      const a = document.createElement('a');
      a.href = url;
      a.download = (filename || '').split('/').pop() || 'anexo';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      aviso.erro('Não foi possível baixar o anexo.');
    }
  };

  const handleViewAttachment = async (id: number) => {
    try {
      const url = await AttachmentService.getAttachmentFileUrl(id);
      window.open(url, '_blank');
    } catch (error: any) {
      aviso.erro('Não foi possível abrir o anexo.');
    }
  };

  const handleRemoveAttachment = async (id: number) => {
    if (!window.confirm('Deseja realmente remover este anexo?')) return;
    try {
      await AttachmentService.removeAttachment(id);
      aviso.ok('Anexo removido.');
      fetchAttachments();
    } catch (error: any) {
      aviso.erro('Não foi possível remover o anexo.');
    }
  };

  // Load tipos de tarefa on mount
  useEffect(() => {
    TipoTarefaService.getAll().then(tipos => {
      setTiposTarefa(tipos);
      if (tipos.length > 0) setModalTipoTarefaId(tipos[0].tipoTarefaId);
    });
  }, []);

  // Load tasks from API
  const fetchTasks = useCallback(async () => {
    if (!deal) return;
    setIsLoadingTasks(true);
    try {
      const data = await TarefaService.getByConversa(deal.id);
      setTasks(data);
    } catch (err) {
      console.error('Erro ao buscar tarefas:', err);
    } finally {
      setIsLoadingTasks(false);
    }
  }, [deal]);

  useEffect(() => {
    if (deal) fetchTasks();
  }, [deal, fetchTasks]);

  // Responsável da tarefa: a lista de usuários da empresa, como no painel Nova oportunidade.
  useEffect(() => {
    apiFetch(`${apiUrl}/api/Usuario/BuscarTodos`)
      .then(r => r.json())
      .then(r => {
        if (r?.sucesso && Array.isArray(r.dados))
          setUsuariosEmpresa(r.dados.map((u: { userId: number; userName: string }) => ({ id: u.userId, nome: u.userName })));
      })
      .catch(() => { /* sem lista, o campo mostra só o usuário logado */ });
  }, []);

  /* ─── Próximo passo: registrar o desfecho da tarefa padrão ─── */

  const proximoPasso = tasks.find(t => t.origem === 'default' && t.estado === 'open') ?? null;

  const registrarDesfecho = async (tarefa: Tarefa, desfecho: DesfechoTarefa, extra: { motivoPerdaId?: number; motivoPerdaDetalhe?: string; tipoLeadId?: number } = {}) => {
    setRegistrandoDesfecho(true);
    const toastId = aviso.carregando('Registrando…');
    try {
      const r = await TarefaService.registrarDesfecho(tarefa.tarefaId, { desfechoId: desfecho.desfechoId, ...extra });
      if (!r.sucesso) {
        // Ganho sem produto: a API recusa com a mesma mensagem do quadro; abre a vinculação.
        if (desfecho.acao === 'move' && desfecho.statusDestinoId === 3 && ehErroDeProduto(r.mensagem)) {
          aviso.fechar(toastId);
          setDesfechoPendente({ tarefa, desfecho });
          setIsWinProductModalOpen(true);
          return;
        }
        throw new Error(r.mensagem || 'Erro ao registrar o desfecho.');
      }
      aviso.ok(r.mensagem || 'Desfecho registrado.', { id: toastId });
      setDesfechoEscolhido('');
      setDesfechoPendente(null);
      if (r.dados?.novoStatusId) setStatusId(r.dados.novoStatusId);
      if (extra.tipoLeadId) setTipoLeadId(extra.tipoLeadId);
      await Promise.all([fetchTasks(), fetchDealInfo(true)]);
      if (r.dados?.novoStatusId === 6) fetchLossHistory();
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível registrar o desfecho.'), { id: toastId });
    } finally {
      setRegistrandoDesfecho(false);
    }
  };

  const confirmarDesfecho = () => {
    if (!proximoPasso || !desfechoEscolhido) return;
    const desfecho = proximoPasso.desfechos.find(d => String(d.desfechoId) === desfechoEscolhido);
    if (!desfecho) return;
    // Perdido pede o motivo, como no cabeçalho. A temperatura é opcional em qualquer etapa.
    if (desfecho.acao === 'lose') { setDesfechoPendente({ tarefa: proximoPasso, desfecho }); setIsLossModalOpen(true); return; }
    registrarDesfecho(proximoPasso, desfecho);
  };

  const openTaskModal = () => {
    setEditingTask(null);
    setModalDescricao('');
    setModalDate(null);
    setModalTime('09:00');
    if (tiposTarefa.length > 0) setModalTipoTarefaId(tiposTarefa[0].tipoTarefaId);
    setModalResponsavelId(currentUser?.id ?? null);
    setIsTaskModalOpen(true);
  };

  const openEditTaskModal = (task: Tarefa) => {
    setEditingTask(task);
    setModalDescricao(task.descricao);
    setModalTipoTarefaId(task.tipoTarefaId);
    setModalResponsavelId(task.responsavelId ?? null);
    if (task.dataRetorno) {
      const d = new Date(task.dataRetorno);
      setModalDate(d);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      setModalTime(`${hh}:${mm}`);
    } else {
      setModalDate(null);
      setModalTime('09:00');
    }
    setIsTaskModalOpen(true);
  };

  const closeTaskModal = () => setIsTaskModalOpen(false);

  const handleReturnShortcut = (hours: number) => {
    const d = new Date();
    d.setTime(d.getTime() + hours * 60 * 60 * 1000);
    setModalDate(d);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    setModalTime(`${hh}:${mm}`);
  };

  const handleModalSubmit = async () => {
    if (!modalDescricao.trim()) {
      aviso.atencao('Informe uma descrição para a tarefa.');
      return;
    }
    if (!modalTipoTarefaId) {
      aviso.atencao('Selecione o tipo da tarefa.');
      return;
    }
    setIsSavingTask(true);
    try {
      let dataRetornoISO: string | null = null;
      if (modalDate) {
        const targetDate = new Date(modalDate);
        if (modalTime) {
          const [hours, minutes] = modalTime.split(':').map(Number);
          targetDate.setHours(hours || 0);
          targetDate.setMinutes(minutes || 0);
          targetDate.setSeconds(0);
          targetDate.setMilliseconds(0);
        }
        dataRetornoISO = targetDate.toISOString();
      }

      if (editingTask) {
        const res = await TarefaService.atualizar({
          tarefaId: editingTask.tarefaId,
          descricao: modalDescricao.trim(),
          estaConcluida: editingTask.estaConcluida,
          tipoTarefaId: modalTipoTarefaId,
          dataRetorno: dataRetornoISO,
          responsavelId: modalResponsavelId,
        });
        if (res.sucesso) {
          aviso.ok('Tarefa atualizada.');
          closeTaskModal();
          fetchTasks();
        } else {
          aviso.erro(res.mensagem || 'Não foi possível atualizar a tarefa.');
        }
      } else {
        const res = await TarefaService.criar({
          descricao: modalDescricao.trim(),
          conversaId: deal?.id ?? null,
          tipoTarefaId: modalTipoTarefaId,
          dataRetorno: dataRetornoISO,
          responsavelId: modalResponsavelId,
        });
        if (res.sucesso) {
          aviso.ok('Tarefa criada.');
          closeTaskModal();
          fetchTasks();
        } else {
          aviso.erro(res.mensagem || 'Não foi possível criar a tarefa.');
        }
      }
    } catch {
      aviso.erro(SEM_SERVIDOR);
    } finally {
      setIsSavingTask(false);
    }
  };

  const handleToggleTask = async (task: Tarefa) => {
    try {
      const res = await TarefaService.atualizar({
        tarefaId: task.tarefaId,
        descricao: task.descricao,
        estaConcluida: !task.estaConcluida,
        tipoTarefaId: task.tipoTarefaId,
        dataRetorno: task.dataRetorno,
      });
      if (res.sucesso) {
        setTasks(prev => prev.map(t => t.tarefaId === task.tarefaId ? { ...t, estaConcluida: !t.estaConcluida } : t));
      } else {
        aviso.erro(res.mensagem || 'Não foi possível atualizar a tarefa.');
      }
    } catch {
      aviso.erro(SEM_SERVIDOR);
    }
  };

  const handleDeleteTask = async (tarefaId: number) => {
    if (!window.confirm('Deseja realmente remover esta tarefa?')) return;
    try {
      const res = await TarefaService.remover(tarefaId);
      if (res.sucesso) {
        setTasks(prev => prev.filter(t => t.tarefaId !== tarefaId));
        aviso.ok('Tarefa removida.');
      } else {
        aviso.erro(res.mensagem || 'Não foi possível remover a tarefa.');
      }
    } catch {
      aviso.erro(SEM_SERVIDOR);
    }
  };

  // Load observations automatically on deal load (since they are needed in the sidebar)
  useEffect(() => {
    if (deal) {
      fetchObservations();
    }
  }, [deal, fetchObservations]);

  /* Conversa ao vivo, igual ao Chat: com a aba aberta, entra no grupo da conversa no hub e
     recarrega quando chega mensagem dela — de qualquer canal (WhatsApp ou Instagram). */
  useEffect(() => {
    if (!isHubConnected || !deal || activeTab !== 'chat') return;
    const idConversa = deal.id;
    const aoReceber = (mensagem: { idConversa?: number }) => {
      if (mensagem?.idConversa === idConversa) fetchMessages(true);
    };
    hubService.invoke('JoinConversationGroup', idConversa).catch(() => {});
    hubService.on('ReceiveNewMessage', aoReceber);
    hubService.on('MensagemApagada', aoReceber);
    return () => {
      hubService.off('ReceiveNewMessage', aoReceber);
      hubService.off('MensagemApagada', aoReceber);
      hubService.invoke('LeaveConversationGroup', idConversa).catch(() => {});
    };
  }, [isHubConnected, deal, activeTab, fetchMessages]);

  // Tab Loaders
  useEffect(() => {
    if (!deal) return;
    if (activeTab === 'chat' && deal.contactId) fetchMessages();
    else if (activeTab === 'attachments' || activeTab === 'notes') fetchAttachments();
    else if (activeTab === 'agenda') fetchAppointments();
  }, [activeTab, deal, fetchMessages, fetchAttachments, fetchAppointments, fetchCustomFields]);

  /* O agente ao lado da tela (06/10): a ficha diz em qual oportunidade a pessoa
     está — é o que faz "ganhei essa" dispensar a pergunta "qual?" — e recarrega
     quando uma ação do agente muda a oportunidade. Vale também na gaveta de
     Tarefas, que monta esta mesma tela. */
  useRegistroDoAgente(deal && id ? {
    tipo: 'oportunidade',
    id: Number(id),
    rotulo: [deal.title, deal.organizacaoNome && deal.organizacaoNome !== deal.title ? deal.organizacaoNome : null]
      .filter(Boolean).join(' · '),
  } : null);

  useEffect(() => {
    const aoAgir = () => { fetchDealInfo(true); fetchDealProducts(); fetchObservations(); };
    window.addEventListener(ACAO_ASSISTENTE_EVENT, aoAgir);
    return () => window.removeEventListener(ACAO_ASSISTENTE_EVENT, aoAgir);
  }, [fetchDealInfo, fetchDealProducts, fetchObservations]);

  // Dados gerais moram na ficha (2026-09-13): carregam junto com o negócio.
  useEffect(() => {
    if (deal) { fetchCustomFields(); fetchLossHistory(); }
  }, [deal, fetchCustomFields, fetchLossHistory]);

  const handleGenerateSummary = async () => {
    if (!deal) return;
    setIsGeneratingSummary(true);
    const toastId = aviso.carregando('Gerando resumo inteligente…');
    try {
      const response = await ChatService.getConversationSummary(deal.id);
      if (response.sucesso) {
        aviso.ok('Resumo gerado.', { id: toastId });
        await fetchDealInfo(true);
      } else {
        aviso.erro(response.mensagem || 'Não foi possível gerar o resumo.', { id: toastId });
      }
    } catch (e: any) {
      console.error('Erro ao gerar resumo:', e);
      aviso.erro('Não foi possível conectar com a IA.', { id: toastId });
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const renderFormattedSummary = (text: string) => {
    if (!text) return null;

    let cleaned = text
      .replace(/Resumo gerado pelo sistema:\s*/gi, '')
      .replace(/Resumo da Conversa:\s*/gi, '')
      .trim();

    const lines = cleaned.split('\n');
    return lines.map((line, index) => {
      let trimmed = line.trim();
      if (!trimmed) return <div key={index} style={{ height: '8px' }} />;

      if (trimmed.startsWith('##')) {
        const title = trimmed.replace(/^##\s*/, '');
        return <h4 key={index} className="summary-section-title">{title}</h4>;
      }

      const boldRegex = /\*\*(.*?)\*\*/g;
      if (boldRegex.test(trimmed)) {
        boldRegex.lastIndex = 0;
        const parts = trimmed.split(/(\*\*.*?\*\*)/g);
        const elements = parts.map((part, idx) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={idx}>{part.slice(2, -2)}</strong>;
          }
          return part;
        });

        const isHeaderStyle = trimmed.startsWith('**') && trimmed.includes(':**');
        if (isHeaderStyle && trimmed.length < 100) {
          return <h4 key={index} className="summary-section-title">{elements}</h4>;
        }

        return <p key={index} className="summary-paragraph">{elements}</p>;
      }

      if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
        const content = trimmed.substring(2);
        return (
          <li key={index} className="summary-list-item">
            {content}
          </li>
        );
      }

      return <p key={index} className="summary-paragraph">{line}</p>;
    });
  };

  const handleSaveInlineValue = async () => {
    if (!deal) return;
    let val = 0;
    if (typeof editValueInput === 'string') {
      const onlyDigits = editValueInput.replace(/\D/g, '');
      val = onlyDigits ? Number(onlyDigits) / 100 : 0;
    } else {
      val = Number(editValueInput) || 0;
    }
    if (isNaN(val) || val < 0) {
      aviso.atencao('Informe um valor válido.');
      return;
    }

    // Optimistic Update
    const formattedPrevious = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(deal.rawValue || 0);
    const formattedNew = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
    
    setDeal(prev => prev ? {
      ...prev,
      value: formattedNew,
      rawValue: val
    } : null);
    setIsEditingValue(false);

    try {
      const autoNote = `Alteração de valor: De ${formattedPrevious} para ${formattedNew}`;

      const result = await OpportunityService.addDetails({
        idConversa: deal.id,
        descricao: autoNote,
        statusNegociacaoId: statusId,
        valor: val
      });

      if (!result.sucesso) {
        throw new Error(result.mensagem || 'Falha ao atualizar valor.');
      }
      aviso.ok('Valor atualizado.');
      fetchDealInfo(true);
      fetchObservations();
    } catch (e: any) {
      aviso.erro(textoDoErro(e, 'Não foi possível atualizar o valor.'));
      fetchDealInfo(true);
    }
  };

  const handleSaveInlineEmail = async () => {
    if (!deal || !deal.contactId) return;
    try {
      const response = await ContactService.update({
        contatoId: deal.contactId,
        nome: contactName || deal.title,
        telefone: contactPhone || deal.phone || '',
        email: editEmailInput
      });
      if (response.sucesso) {
        aviso.ok('E-mail atualizado.');
        setIsEditingEmail(false);
        setContactEmail(editEmailInput);
      } else {
        throw new Error(response.mensagem || 'Erro ao atualizar.');
      }
    } catch (e: any) {
      aviso.erro(textoDoErro(e, 'Não foi possível atualizar o e-mail.'));
    }
  };

  // Campaign Info
  useEffect(() => {
    const fetchCampaignInfo = async () => {
      if (!deal) return;
      if (deal.campanha !== undefined) {
        setCampaignInfo({
          campanha: deal.campanha,
          idCampanha: deal.idCampanha ?? null,
          nomeCampanha: deal.nomeCampanha || ''
        });
        if (deal.tipoLeadId !== undefined) {
          setTipoLeadId(deal.tipoLeadId);
        }
        return;
      }
      try {
        const response = await apiFetch(`${apiUrl}/api/Chat/ConversasPorVendedor`);
        const result = await response.json();
        if (result.sucesso && Array.isArray(result.dados)) {
          const conv = result.dados.find((c: any) => c.idConversa === deal.id);
          if (conv) {
            setCampaignInfo({
              campanha: conv.campanha,
              idCampanha: conv.idCampanha,
              nomeCampanha: conv.nomeCampanha || ''
            });
            setTipoLeadId(conv.tipoLeadId || 0);
          } else {
            setCampaignInfo({
              campanha: false,
              idCampanha: null,
              nomeCampanha: ''
            });
          }
        } else {
          setCampaignInfo({
            campanha: false,
            idCampanha: null,
            nomeCampanha: ''
          });
        }
      } catch (err) {
        console.error("Erro ao buscar informações de campanha:", err);
        setCampaignInfo({
          campanha: false,
          idCampanha: null,
          nomeCampanha: ''
        });
      }
    };
    fetchCampaignInfo();
  }, [deal]);

  /* A lista de tipos é da empresa e muda pouco: carrega uma vez, com a ficha. Sem a
     funcionalidade ligada nem a chamada acontece — a empresa não tem o campo. */
  const [tiposContato, setTiposContato] = useState<TipoContato[]>([]);
  const [isUpdatingTipoContato, setIsUpdatingTipoContato] = useState(false);
  const usaTipoContato = temFuncionalidade('tipo_contato');

  useEffect(() => {
    if (!usaTipoContato) return;
    let vivo = true;
    TipoContatoService.getAll()
      .then(r => { if (vivo && r.sucesso) setTiposContato(r.dados || []); })
      .catch(() => { /* a ficha não deixa de abrir porque o cadastro não respondeu */ });
    return () => { vivo = false; };
  }, [usaTipoContato]);

  const handleTipoContatoChange = async (valor: string) => {
    if (!deal?.contactId) return;
    const novoId = valor === '0' ? null : parseInt(valor);
    setIsUpdatingTipoContato(true);
    try {
      const r = await TipoContatoService.definirNoContato(deal.contactId, novoId);
      aviso.ok(r.mensagem || 'Tipo do contato atualizado.');
      /* Recarrega porque o tipo pode ter tirado o contato do funil, e isso muda a ficha
         inteira (etapa, temperatura e valor somem para quem está fora). */
      fetchDealInfo(true);
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível atualizar o tipo do contato.'));
    } finally {
      setIsUpdatingTipoContato(false);
    }
  };

  const handleLeadTypeChange = async (newTipoLeadIdStr: string) => {
    if (!deal) return;
    const newTipoLeadId = parseInt(newTipoLeadIdStr);
    setIsUpdatingLeadType(true);
    try {
      await ChatService.atualizarTipoLead(deal.id, newTipoLeadId);
      setTipoLeadId(newTipoLeadId);
      aviso.ok("Temperatura do lead atualizada.");
      fetchDealInfo(true);
    } catch (error: any) {
      aviso.erro(textoDoErro(error, "Não foi possível atualizar a temperatura do lead."));
    } finally {
      setIsUpdatingLeadType(false);
    }
  };

  // Organização vinculada: os dados do bloco Organização da ficha.
  useEffect(() => {
    const id = deal?.organizacaoId ?? null;
    if (!id) { setOrganizacao(null); return; }
    let cancelado = false;
    OrganizationService.getById(id)
      .then(r => { if (!cancelado && r.sucesso) setOrganizacao(r.dados); })
      .catch(() => { /* a ficha mostra só o nome do picker */ });
    return () => { cancelado = true; };
  }, [deal?.organizacaoId]);

  // Contact Email
  useEffect(() => {
    const fetchEmail = async () => {
      if (deal && deal.contactId) {
        try {
          const response = await ContactService.getById(deal.contactId);
          if (response.sucesso) {
            setContactEmail(response.dados.email || '');
            setContactName(response.dados.nome || '');
            setContactPhone(response.dados.telefone || '');
          } else {
            setContactEmail('Email não cadastrado');
          }
        } catch (error) {
          setContactEmail('Email não cadastrado');
        }
      }
    };
    fetchEmail();
  }, [deal]);

  const voltar = (
    <button type="button" className="dd-voltar" onClick={() => navigate('/pipeline')} aria-label="Voltar para Oportunidades" title="Voltar para Oportunidades">
      <LuArrowLeft size={15} />
    </button>
  );

  /* Abas do painel (pedido dele, 2026-09-23): saíram da segunda faixa do cabeçalho
     e viraram a primeira linha DENTRO do painel. Ficam fora do bloco de cada aba
     porque a aba WhatsApp não tem cabeçalho próprio — se a tira morasse no
     cabeçalho de cada aba, ela sumiria justamente lá.
     No mesmo dia ele mandou economizar altura: o título da aba saiu (a aba ativa
     já o diz) e a ação da aba subiu para esta linha, à direita. Uma linha só, e
     todas as abas com a mesma casca. Função e não constante porque o esqueleto de
     carregamento monta a tira sem ação — o deal ainda não chegou. */
  const tiraAbas = (acao?: ReactNode) => (
    <div className="dd-tabs">
      <nav className="dd-tabs-nav" aria-label="Seções da oportunidade">
        {abas.map(aba => (
          <button key={aba.id} type="button"
            className={`dd-tab ${activeTab === aba.id ? 'is-active' : ''}`}
            aria-current={activeTab === aba.id ? 'page' : undefined}
            onClick={() => setActiveTab(aba.id as typeof activeTab)}>
            <span>{aba.label}</span>
          </button>
        ))}
      </nav>
      {acao && <div className="dd-tabs-act">{acao}</div>}
    </div>
  );

  if (isLoadingDeal) {
    return (
      <div className="page-shell dd-screen pl-screen lk-screen">
        {/* O esqueleto ocupa a faixa da régua para o cabeçalho não mudar de altura
            quando a oportunidade chega. */}
        <PageHeader leading={voltar} title="Carregando…" subtitle="Oportunidade"
          band={<div className="pl-skel" style={{ height: 34 }} />} />
        <div className="page-body dd-body">
          <div className="dd-layout">
            <aside className="dd-ficha">
              <div className="pl-skel" style={{ height: 22, width: '60%' }} />
              <div className="pl-skel" style={{ height: 14, width: '80%', marginTop: 14 }} />
              <div className="pl-skel" style={{ height: 14, width: '70%', marginTop: 8 }} />
              <div className="pl-skel" style={{ height: 14, width: '75%', marginTop: 8 }} />
              <div className="pl-skel" style={{ height: 120, marginTop: 24 }} />
            </aside>
            <section className="dd-pane">
              {tiraAbas()}
              <div className="dd-pane-body">
                {[1, 2, 3].map(i => <div key={i} className="pl-skel" style={{ height: 56, marginBottom: 10 }} />)}
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  if (!deal) return null;

  const summaryNote = observations.find(obs =>
    obs.content && (obs.content.includes('Resumo gerado pelo sistema:') || obs.content.includes('Resumo da Conversa:'))
  );
  const filteredNotes = observations.filter(obs =>
    !obs.content || !(obs.content.includes('Resumo gerado pelo sistema:') || obs.content.includes('Resumo da Conversa:'))
  );

  const canalInfo = canal(deal.idOrigemOportunidade);
  const semDono = !deal.ownerId && (!deal.owner || deal.owner === 'IA' || deal.owner === 'Sistema');
  /* A mesma regra da Pipeline: com dono, só quem tem `conversas.todos` assume, e
     nunca venda fechada (Ganho 3 / Perdido 6) — mudaria de quem é a venda nas metas. */
  const adminPodeTomar = !semDono && hasPermission(permissions, [PERMISSOES.conversasTodos]) && currentUser !== null
    && deal.ownerId !== currentUser.id && statusId !== 3 && statusId !== 6;
  /* Excluir oportunidade e so do administrador da empresa — a mesma trava da Pipeline, aqui
     porque a ficha chama o mesmo DELETE. Papel, nao permissao de catalogo. */
  const podeExcluir = ehAdministradorDaEmpresa();
  // Criada à mão (origem 6): não nasce de conversa, então não há mensagens para o resumo.
  const criadaManual = deal.idOrigemOportunidade === 6;
  const etapaAtual = etapasDaFicha().find(e => e.value === String(statusId));
  // A API devolve o histórico do mais novo para o mais antigo; o primeiro é a perda vigente.
  const perdaAtual = statusId === 6 ? lossReasonHistory[0] ?? null : null;
  const perdasAnteriores = perdaAtual ? lossReasonHistory.slice(1) : lossReasonHistory;
  const subtitulo = [
    'Oportunidade',
    deal.organizacaoNome && deal.organizacaoNome !== deal.title ? deal.organizacaoNome : null,
    canalInfo.nome,
    deal.campanha ? (campaignInfo?.nomeCampanha || 'Campanha') : null,
    `criada em ${deal.date}`,
  ].filter(Boolean).join(' · ');

  const produtosFiltrados = availableProducts.filter(p =>
    !productSearchQuery ||
    p.nome.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
    p.codigo.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
    (p.marca && p.marca.toLowerCase().includes(productSearchQuery.toLowerCase()))
  );

  const abrirPainelProduto = () => {
    setIsProductModalOpen(true);
    setSelectedProductId(null);
    setProductQuantity(1);
    setProductCustomPrice('');
    setProductSearchQuery('');
  };

  const vincularProduto = async () => {
    if (!selectedProductId) return;
    const prod = availableProducts.find(p => p.produtoId === selectedProductId);
    if (!prod) return;
    const price = parseFloat(productCustomPrice) >= 0 ? parseFloat(productCustomPrice) : prod.preco;
    try {
      await ConversaProdutoService.vincular(Number(id), {
        produtoId: prod.produtoId,
        quantidade: productQuantity,
        precoUnitarioNegociado: price,
      });
      await fetchDealProducts();
      fetchDealInfo(true);
      setIsProductModalOpen(false);
      aviso.ok(`${prod.nome} vinculado à oportunidade.`);
    } catch (err: any) {
      aviso.erro(textoDoErro(err, 'Não foi possível vincular o produto.'));
    }
  };

  const alterarQuantidade = async (p: DealProduct, novaQtd: number) => {
    setDealProducts(prev => prev.map(dp =>
      dp.oportunidadeProdutoId === p.oportunidadeProdutoId
        ? { ...dp, quantidade: novaQtd, precoTotal: novaQtd * dp.precoUnitarioNegociado }
        : dp
    ));
    try {
      await ConversaProdutoService.atualizar(Number(id), p.oportunidadeProdutoId, {
        quantidade: novaQtd,
        precoUnitarioNegociado: p.precoUnitarioNegociado,
      });
      fetchDealInfo(true);
    } catch (err: any) {
      aviso.erro(textoDoErro(err, 'Não foi possível atualizar a quantidade.'));
      fetchDealProducts();
    }
  };

  const removerProduto = async (p: DealProduct) => {
    setDealProducts(prev => prev.filter(dp => dp.oportunidadeProdutoId !== p.oportunidadeProdutoId));
    try {
      await ConversaProdutoService.remover(Number(id), p.oportunidadeProdutoId);
      aviso.ok('Produto removido.');
      fetchDealInfo(true);
    } catch (err: any) {
      aviso.erro(textoDoErro(err, 'Não foi possível remover o produto.'));
      fetchDealProducts();
    }
  };

  const campoPersonalizadoInput = (field: CampoPersonalizadoValor) => {
    const v = customFieldValues[field.campoPersonalizadoId] ?? '';
    const set = (nv: string) => handleCustomFieldChange(field.campoPersonalizadoId, nv);
    if (field.tipo === TipoCampoPersonalizado.Selecao) {
      return <Select value={v} onChange={set} disabled={isSavingCustomFields} placeholder="Selecionar"
        options={(field.opcoes ?? []).map(op => ({ value: op, label: op }))} />;
    }
    if (field.tipo === TipoCampoPersonalizado.Booleano) {
      return <Select value={v} onChange={set} disabled={isSavingCustomFields} placeholder="Selecionar"
        options={[{ value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }]} />;
    }
    const tipo = field.tipo === TipoCampoPersonalizado.Data ? 'date' : field.tipo === TipoCampoPersonalizado.Numero ? 'number' : 'text';
    return <input type={tipo} className="dd-input" value={v} onChange={e => set(e.target.value)} disabled={isSavingCustomFields} />;
  };

  const alterarOrganizacao = async (organizacaoId: number | null, nome: string | null) => {
    if (!deal) return;
    setIsSavingOrg(true);
    try {
      await OpportunityService.atualizarOrganizacao(deal.id, organizacaoId);
      setDeal(prev => (prev ? { ...prev, organizacaoId, organizacaoNome: nome ?? undefined } : prev));
      aviso.ok(organizacaoId ? 'Organização vinculada ao negócio.' : 'Organização removida do negócio.');
      fetchDealInfo(true);
    } catch (e: any) {
      aviso.erro(textoDoErro(e, 'Não foi possível atualizar a organização.'));
    } finally {
      setIsSavingOrg(false);
    }
  };

  /** Anotações antigas são texto puro; as novas saem do editor como HTML. */
  // Qualquer tag de abertura conta — anotação que começa por uma imagem também é HTML.
  const ehHtml = (texto: string) => /^\s*<[a-z][a-z0-9-]*(\s[^>]*)?>/i.test(texto);

  const receberArquivos = (arquivos: File[]) => setPendingFiles(prev => [...prev, ...arquivos]);

  /**
   * Formulário da anotação, para criar e para editar: editor + área de anexos
   * que aceita clique, arrastar e colar (colar no editor também cai aqui).
   *
   * É FUNÇÃO, não componente: um componente declarado dentro do render nasce
   * com identidade nova a cada render, e o editor seria desmontado a cada
   * tecla — foi assim que "Cliente pediu desconto" virou "Cidet10sx" na sonda.
   */
  const formularioAnotacao = (titulo: string) => (
    <div
      className="dd-noteform"
      onDragOver={e => { e.preventDefault(); }}
      onDrop={e => { e.preventDefault(); receberArquivos(Array.from(e.dataTransfer.files)); }}
    >
      <div className="dd-noteform-head">
        <span className="dd-sec-title" style={{ margin: 0 }}>{titulo}</span>
        <span className="dd-hint" style={{ margin: 0 }}>Cole ou arraste: imagem entra no texto, outro arquivo vira anexo</span>
      </div>
      <ObservationEditor
        key={editingNoteId ?? 'nova'}
        valor={detailsDescription}
        onChange={setDetailsDescription}
        onFiles={receberArquivos}
        onImage={enviarImagemDaAnotacao}
        disabled={isSavingDetails}
        autoFocus
      />
      <div className="dd-chips">
        {pendingFiles.map((arquivo, i) => (
          <span key={`${arquivo.name}-${i}`} className="dd-chip is-pending">
            {iconeAnexo(tipoDoArquivo(arquivo))}
            <span className="dd-chip-name" title={arquivo.name}>{arquivo.name}</span>
            <button type="button" className="dd-chip-act" aria-label="Remover da lista" title="Remover da lista"
              onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}><LuX size={12} /></button>
          </span>
        ))}
        <label className="dd-chip is-add">
          <LuPaperclip size={12} /> <span>Anexar arquivo</span>
          <input type="file" multiple onChange={e => { receberArquivos(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
        </label>
      </div>
      <div className="dd-form-acts">
        <button type="button" className="lk-btn" onClick={() => fecharFormularioAnotacao(true)} disabled={isSavingDetails}>Cancelar</button>
        <button type="button" className="lk-btn is-primary" onClick={handleSaveDetails} disabled={isSavingDetails}>
          {isSavingDetails ? 'Salvando…' : editingNoteId ? 'Salvar' : 'Adicionar'}
        </button>
      </div>
    </div>
  );

  const chatBloqueado = [1, 3, 6, 8].includes(statusId) || (currentUser !== null && currentUser.nome !== deal.owner);

  /* ─── Régua de etapas (pedido dele, 2026-09-15): onde a oportunidade está, e a
     porta única para trocar — o Select de etapa saiu do cabeçalho. Novos (IA)
     não é clicável: oportunidade não volta para a IA. Perdido só aparece no fim
     quando é a etapa atual; senão o fim é Ganho.
     Em 2026-09-23 ela subiu para a segunda faixa do cabeçalho (slot `band`), no
     lugar das abas, que desceram para dentro do painel: a etapa é o estado da
     oportunidade inteira, então fica junto do título; a aba é recorte do
     conteúdo, então fica junto do conteúdo. ─── */
  /* O select de etapa do cabeçalho oferece as mesmas etapas em que a régua deixa clicar,
     MAIS "Perdido": a régua só mostra Perdido quando o negócio já está perdido, então até
     aqui registrar a perda dependia do próximo passo da tarefa. "Atendimento IA" e "IA
     encerrada" ficam de fora — oportunidade não volta para a IA —, mas entram desabilitadas
     quando o negócio está numa delas, senão o select não teria o que mostrar. */
  const opcoesEtapa = (() => {
    const etapas = etapasDaFicha();
    // O miolo inteiro, por maior que ele fique, mais os dois desfechos. As do robô ficam fora.
    const oferecidas = gruposDoFunil()
      .filter(g => g.cor !== 'ia')
      .flatMap(g => g.statusIds.map(String));
    const base = etapas.filter(e => oferecidas.includes(e.value));
    const atual = etapas.find(e => e.value === String(statusId));
    return atual && !oferecidas.includes(atual.value) ? [{ ...atual, disabled: true }, ...base] : base;
  })();

  const reguaEtapas = (() => {
    const grupos = gruposDoFunil();
    const abertas = grupos
      .filter(g => g.cor !== 'win' && g.cor !== 'lost')
      .map(g => ({ id: g.alvo, ids: g.statusIds, label: g.title, clicavel: g.cor !== 'ia' }));
    const ganho = grupos.find(g => g.cor === 'win');
    const perdido = grupos.find(g => g.cor === 'lost');
    const fim = perdido && perdido.statusIds.includes(statusId)
      ? { id: perdido.alvo, ids: perdido.statusIds, label: perdido.title, clicavel: true }
      : { id: ganho?.alvo ?? 3, ids: ganho?.statusIds ?? [3], label: ganho?.title ?? 'Ganho', clicavel: true };
    const segs = [...abertas, fim];
    const atual = segs.findIndex(s => s.ids.includes(statusId));
    return (
      <nav className={`dd-etapas ${isUpdatingStatus ? 'is-busy' : ''}`} aria-label="Etapas da oportunidade">
        {segs.map((s, i) => {
          const estado = i < atual ? 'is-done' : i === atual ? 'is-current' : 'is-next';
          const cor = s.id === 6 ? 'is-lost' : s.id === 3 ? 'is-won' : '';
          return (
            <button key={s.id} type="button" className={`dd-etapa ${estado} ${cor}`}
              aria-current={i === atual ? 'step' : undefined}
              disabled={!s.clicavel || i === atual || isUpdatingStatus}
              title={i === atual ? 'Etapa atual' : s.clicavel ? `Mover para ${s.label}` : 'Uma oportunidade não volta para a IA'}
              onClick={() => handleStatusChange(String(s.id))}>
              <span>{s.label}</span>
            </button>
          );
        })}
      </nav>
    );
  })();

  /* Contato fora do funil: no lugar da régua, a faixa que diz isso e o caminho de volta. */
  const faixaForaDoFunil = (
    <div className="dd-fora">
      <span>Fora do funil — esta conversa não conta na Pipeline nem nas métricas.</span>
      {deal.contactId ? (
        <button type="button" className="lk-btn" onClick={() => alterarFunil(false)} disabled={mudandoFunil}>
          {mudandoFunil ? 'Aguarde…' : 'Levar para o funil'}
        </button>
      ) : null}
    </div>
  );

  /* A ação da aba ativa, que vai à direita da tira de abas. A aba WhatsApp não tem
     ação própria (o que se faz lá é escrever, e a caixa de mensagem já está na
     tela), então a tira fica só com as abas. */
  const acaoAba: ReactNode = (
    activeTab === 'agenda' ? (
      <button type="button" className="lk-btn is-primary" onClick={openTaskModal}><LuPlus size={14} /> Nova tarefa</button>
    ) : activeTab === 'notes' ? (
      !showAddDetails && editingNoteId === null && (
        <button type="button" className="lk-btn is-primary" onClick={() => { setDetailsDescription(''); setPendingFiles([]); setShowAddDetails(true); }}>
          <LuPlus size={14} /> Nova anotação
        </button>
      )
    ) : activeTab === 'attachments' ? (
      <label className="lk-btn is-primary dd-upload">
        <LuPaperclip size={14} /> Enviar arquivo
        <input type="file" onChange={handleFileUpload} />
      </label>
    ) : activeTab === 'products' ? (
      <button type="button" className="lk-btn is-primary" onClick={abrirPainelProduto}><LuPlus size={14} /> Adicionar produto</button>
    ) : activeTab === 'documents' ? (
      <button type="button" className="lk-btn is-primary" onClick={() => setEmitirDocAberto(true)}><LuFileText size={14} /> Emitir documento</button>
    ) : null
  ) || null;

  /* A ficha (Negócio, Origem, Levantadas, Contato, Informações adicionais, Organização e Resumo IA).
     No computador é a coluna da esquerda; no celular ela vira a aba Dados do painel: empilhada
     acima das abas, empurrava a tira para 1500–2000px de rolagem e ninguém via a tarefa de cara. */
  const ficha = (
    <aside className="dd-ficha">
      <section className="dd-sec">
        <h3 className="dd-sec-title">Negócio</h3>
        <div className="dd-kv">
          {!deal.foraDoFunil && <span className="dd-k">Valor</span>}
          {deal.foraDoFunil ? null : isEditingValue ? (
            <span className="dd-v dd-inline-edit">
              <input type="text" value={editValueInput} onChange={handleValueInputChange} className="dd-input" placeholder="R$ 0,00" autoFocus inputMode="numeric" />
              <button type="button" className="lk-btn is-primary" onClick={handleSaveInlineValue}>Salvar</button>
              <button type="button" className="lk-btn" onClick={() => setIsEditingValue(false)}>Cancelar</button>
            </span>
          ) : (
            <span className="dd-v">
              <strong className="dd-valor">{deal.value}</strong>
              <button type="button" className="dd-edit" aria-label="Editar valor" title="Editar valor"
                onClick={() => { setIsEditingValue(true); setEditValueInput(formatCurrency(deal.rawValue || 0)); }}>
                <LuPencil size={12} />
              </button>
            </span>
          )}

          {!deal.foraDoFunil && <span className="dd-k">Etapa</span>}
          {!deal.foraDoFunil && <span className="dd-v"><span className="pl-etapa"><i className="pl-dot" style={{ backgroundColor: etapaAtual?.dot }} aria-hidden="true" /><span>{etapaAtual?.label ?? '—'}</span></span></span>}

          <span className="dd-k">Responsável</span>
          <span className="dd-v">{semDono ? <span className="dd-empty">sem responsável</span> : deal.owner}</span>

          {deal.dataFechamentoVenda && (
            <>
              <span className="dd-k">Fechada em</span>
              <span className="dd-v">{new Date(deal.dataFechamentoVenda).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            </>
          )}

          {statusId === 6 && (
            <>
              <span className="dd-k">Motivo</span>
              <span className="dd-v dd-perda">
                {perdaAtual ? (
                  <>
                    <span>{perdaAtual.descricaoMotivo}</span>
                    {perdaAtual.detalhesMotivo && <span className="dd-perda-detalhe">{perdaAtual.detalhesMotivo}</span>}
                  </>
                ) : <span className="dd-empty">não registrado</span>}
              </span>
            </>
          )}
        </div>
      </section>

      {perdasAnteriores.length > 0 && (
        <section className="dd-sec">
          <h3 className="dd-sec-title">Perdas anteriores</h3>
          <ul className="dd-perdas">
            {perdasAnteriores.map((item, idx) => (
              <li key={`${item.idMotivoPerda}-${item.dataMotivo}-${idx}`}>
                <div className="dd-perda-linha"><span>{item.descricaoMotivo}</span><time>{dataHora(item.dataMotivo)}</time></div>
                {item.detalhesMotivo && <span className="dd-perda-detalhe">{item.detalhesMotivo}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Origem: de onde a pessoa veio, logo abaixo do negócio. Saiu do bloco Contato, onde
          era a última linha e o anúncio não cabia (225px). A regra é a mesma de antes, em
          quatro níveis: anúncio, origem de marketing (RD, webhook, página de captura),
          disparo, orgânica. O anúncio aparece com a funcionalidade Anúncios ligada ou não:
          ela libera a tela de desempenho, não a origem do lead (ele, 17/10/2026). */}
      <section className="dd-sec">
        <h3 className="dd-sec-title">Origem</h3>
        {deal.atribuicaoAnuncio && (
          <CartaoOrigemAnuncio atribuicao={deal.atribuicaoAnuncio} />
        )}
        <div className="dd-kv">
          <span className="dd-k">Canal</span>
          <span className="dd-v"><span className="pl-etapa">{canalInfo.glifo}<span>{canalInfo.nome}</span></span></span>

          {!deal.atribuicaoAnuncio && (
            <>
              <span className="dd-k">Origem</span>
              <span className="dd-v">
                {rotuloOrigemMarketing(deal.origemMarketing) ?? (
                  campaignInfo === null
                    ? <span className="dd-empty">carregando…</span>
                    : campaignInfo.campanha
                      ? <span className="pl-etapa"><LuMegaphone size={13} /><span>Disparo: {campaignInfo.nomeCampanha || `#${campaignInfo.idCampanha}`}</span></span>
                      : 'Entrada orgânica')}
              </span>
            </>
          )}
        </div>
      </section>

      {/* Levantadas de mão: cada vez que a PESSOA procurou a empresa — conversão do site,
          anúncio, resposta a disparo, e a primeira mensagem dela em cada conversa. Sinais do
          mesmo gesto (o clique no botão do site dispara conversão e mensagem com segundos de
          diferença) chegam da API já fundidos numa linha só.
          Desenho: docs/propostas/pipeline/levantadas-de-mao.html (12/10/2026).
          A linha "Origem" continua no bloco Contato de propósito: ela é um fallback de quatro
          níveis (atribuição de anúncio, origem de marketing, disparo, orgânica) e esta seção
          ainda não guarda anúncio nem disparo — tirá-la faria esses negócios perderem a origem. */}
      <section className="dd-sec">
        <div className="dd-sec-head">
          <h3 className="dd-sec-title">
            Levantadas de mão
            <InfoTitulo rotulo="O que conta como levantada de mão">
              <p>Cada vez que a pessoa procurou a empresa: conversão de formulário ou botão do
              site, clique de anúncio, resposta a disparo e a primeira mensagem dela numa conversa.</p>
              <p>Sinais do mesmo gesto, dentro de 30 minutos, contam como um só. Negociação criada
              no CRM de outra ferramenta não conta — é a ferramenta falando, não a pessoa.</p>
              <p>A lista é da pessoa, então pode incluir contatos anteriores a este negócio.</p>
            </InfoTitulo>
          </h3>
          {handRaises.length > 0 && <span className="dd-lm-cont">{handRaises.length}</span>}
        </div>
        {isLoadingHandRaises ? (
          <div className="pl-skel" style={{ height: 52 }} />
        ) : handRaises.length === 0 ? (
          <p className="dd-lm-vazio">
            {criadaManual || deal.campanha
              ? <>Este negócio foi <b>cadastrado pela equipe</b> — a pessoa ainda não procurou a empresa.</>
              : <>Ninguém levantou a mão neste negócio ainda.</>}
            {' '}
            <span className="dd-empty">A primeira vez que ela chamar aparece aqui.</span>
          </p>
        ) : (
          <ol className="dd-lm-lista">
            {(verTodasLevantadas ? handRaises : handRaises.slice(0, 3)).map((lm, i) => (
              <li className={`dd-lm-item${i > 0 ? ' is-antiga' : ''}`} key={`${lm.quando}-${lm.idConversa ?? 'x'}-${i}`}>
                <span className="dd-lm-trilho" aria-hidden="true">
                  <span className="dd-lm-ponto" />
                  <span className="dd-lm-fio" />
                </span>
                <div className="dd-lm-corpo">
                  <time className="dd-lm-quando" dateTime={lm.quando}>{dataHora(lm.quando)}</time>
                  <span className="dd-lm-porta">{canal(lm.idCanal).glifo}<span>{portaDaLevantada(lm)}</span></span>
                  {/* "sem origem registrada" explica o caso quando a pessoa procurou UMA vez;
                      repetido em cada linha de uma lista longa vira ruído, e a ausência da
                      linha já diz a mesma coisa. */}
                  {(lm.origem || handRaises.length === 1) && (
                    <span className="dd-lm-origem">
                      {lm.origem
                        ? <>{lm.origem}{lm.utmCampaign && <><br /><span className="dd-lm-campanha">{lm.utmCampaign}</span></>}</>
                        : <span className="dd-empty">sem origem registrada</span>}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
        {/* A ficha tem 360px e divide a coluna com Negócio, Dados gerais, Contato e
            Organização. Medido numa base de desenvolvimento: um contato com 24 levantadas
            deixa a seção com 1512px — o dobro da altura visível da ficha —, empurrando todo
            o resto para fora da tela. As três mais recentes são as que explicam o negócio
            de hoje; o resto fica a um clique, sem sair da ficha. */}
        {!isLoadingHandRaises && handRaises.length > 3 && (
          <button type="button" className="dd-link dd-lm-mais"
            onClick={() => setVerTodasLevantadas(v => !v)}>
            {verTodasLevantadas ? 'Ver menos' : `Ver todas (${handRaises.length})`}
          </button>
        )}
      </section>

      {/* Contato: só a pessoa. Canal e origem foram para a seção Origem (16/10/2026). */}
      <section className="dd-sec">
        <h3 className="dd-sec-title">Contato</h3>
        <div className="dd-kv">
          <span className="dd-k">Nome</span>
          <span className="dd-v">{contactName || (deal.contactId ? <span className="dd-empty">sem nome</span> : <span className="dd-empty">sem contato vinculado</span>)}</span>

          <span className="dd-k">Telefone</span>
          <span className="dd-v">{telefoneLegivel(contactPhone || deal.phone) || <span className="dd-empty">não informado</span>}</span>

          <span className="dd-k">E-mail</span>
          {isEditingEmail ? (
            <span className="dd-v dd-inline-edit">
              <input type="email" value={editEmailInput} onChange={e => setEditEmailInput(e.target.value)} className="dd-input" autoFocus />
              <button type="button" className="lk-btn is-primary" onClick={handleSaveInlineEmail}>Salvar</button>
              <button type="button" className="lk-btn" onClick={() => setIsEditingEmail(false)}>Cancelar</button>
            </span>
          ) : (
            <span className="dd-v">
              {contactEmail || <span className="dd-empty">não informado</span>}
              {!!deal.contactId && (
                <button type="button" className="dd-edit" aria-label="Editar e-mail" title="Editar e-mail"
                  onClick={() => { setIsEditingEmail(true); setEditEmailInput(contactEmail); }}>
                  <LuPencil size={12} />
                </button>
              )}
            </span>
          )}

          {usaTipoContato && tiposContato.length > 0 && (
            <>
              <span className="dd-k">Tipo</span>
              <span className="dd-v">
                {deal.contactId ? (
                  <Select
                    size="control" block={false}
                    value={String(deal.tipoContatoId ?? 0)}
                    options={[
                      { value: '0', label: 'Sem tipo' },
                      ...tiposContato.map(t => ({ value: String(t.tipoContatoId), label: t.nome })),
                    ]}
                    onChange={handleTipoContatoChange}
                    disabled={isUpdatingTipoContato}
                    ariaLabel="Tipo do contato" title="O que este contato é para a empresa" />
                ) : <span className="dd-empty">sem contato vinculado</span>}
              </span>
            </>
          )}

        </div>
      </section>

      {/* Informações adicionais: os campos personalizados que a empresa criou para o
          negócio (eram "Dados gerais"). Uma linha por campo, como Valor e E-mail, com o
          lápis ao lado: edita aquele campo e salva — sem o "Editar/Salvar" do bloco nem
          as caixas desabilitadas. Empresa sem campo não vê a seção, nem o administrador:
          criar campo é em Configurações › Vendas › Campos personalizados. Medido em
          16/10/2026: em produção, só a própria Brik tinha campo; para todo cliente a
          seção era um "Nenhum campo personalizado" no meio da ficha. */}
      {(isLoadingCustomFields || customFields.length > 0) && (
        <section className="dd-sec">
          <h3 className="dd-sec-title">Informações adicionais</h3>
          {isLoadingCustomFields ? (
            <>
              <div className="pl-skel" style={{ height: 20 }} />
              <div className="pl-skel" style={{ height: 20, marginTop: 8 }} />
            </>
          ) : (
            <div className="dd-kv">
              {customFields.map(field => {
                const id = field.campoPersonalizadoId;
                const legivel = valorLegivelDoCampo(field, customFieldValues[id]);
                return (
                  <React.Fragment key={id}>
                    <span className="dd-k">{field.nome}{field.obrigatorio ? ' *' : ''}</span>
                    {campoEmEdicao === id ? (
                      <span className="dd-v dd-inline-edit">
                        {campoPersonalizadoInput(field)}
                        <button type="button" className="lk-btn is-primary" onClick={() => handleSaveCustomFields(id)} disabled={isSavingCustomFields}>
                          {isSavingCustomFields ? 'Salvando…' : 'Salvar'}
                        </button>
                        <button type="button" className="lk-btn" onClick={() => cancelarCampo(id)} disabled={isSavingCustomFields}>Cancelar</button>
                      </span>
                    ) : (
                      <span className="dd-v">
                        {legivel || <span className="dd-empty">não informado</span>}
                        <button type="button" className="dd-edit" aria-label={`Editar ${field.nome}`} title={`Editar ${field.nome}`}
                          onClick={() => setCampoEmEdicao(id)} disabled={campoEmEdicao !== null}>
                          <LuPencil size={12} />
                        </button>
                      </span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </section>
      )}

      <section className="dd-sec">
        <h3 className="dd-sec-title">Organização</h3>
        <div className="dd-kv">
          <span className="dd-k">Empresa</span>
          <span className="dd-v">
            <OrganizationPicker
              id="dd-org-pick"
              value={deal.organizacaoId ?? null}
              valueLabel={deal.organizacaoNome ?? null}
              disabled={isSavingOrg}
              onChange={alterarOrganizacao}
            />
          </span>
          {deal.organizacaoId && organizacao && (
            <>
              {organizacao.nomeFantasia && organizacao.nomeFantasia !== organizacao.nome && (
                <><span className="dd-k">Fantasia</span><span className="dd-v">{organizacao.nomeFantasia}</span></>
              )}
              {/* Só o que está preenchido: linha "não informado" não ajuda o vendedor e
                  empurra o Resumo IA para fora da tela. O cadastro inteiro está na tela da
                  organização (16/10/2026). */}
              {organizacao.cnpj && (
                <><span className="dd-k">CNPJ</span><span className="dd-v dd-mono">{formatarCnpj(organizacao.cnpj)}</span></>
              )}
              {organizacao.site && (
                <><span className="dd-k">Site</span><span className="dd-v">
                  <a className="dd-link-ext" href={/^https?:\/\//i.test(organizacao.site) ? organizacao.site : `https://${organizacao.site}`} target="_blank" rel="noreferrer">{organizacao.site.replace(/^https?:\/\//i, '')}</a>
                </span></>
              )}
              {organizacao.email && (
                <><span className="dd-k">E-mail</span><span className="dd-v">{organizacao.email}</span></>
              )}
              {organizacao.telefone && (
                <><span className="dd-k">Telefone</span><span className="dd-v">{telefoneLegivel(organizacao.telefone)}</span></>
              )}
              {(organizacao.cidade || organizacao.estado) && (
                <><span className="dd-k">Cidade</span><span className="dd-v">{[organizacao.cidade, organizacao.estado].filter(Boolean).join(' - ')}</span></>
              )}
              {organizacao.segmento && (
                <><span className="dd-k">Segmento</span><span className="dd-v">{organizacao.segmento}</span></>
              )}
              <span className="dd-k">Pessoas</span>
              <span className="dd-v">{organizacao.quantidadePessoas} {organizacao.quantidadePessoas === 1 ? 'contato' : 'contatos'}</span>
            </>
          )}
          {deal.organizacaoId && !organizacao && (
            <><span className="dd-k">Dados</span><span className="dd-v"><span className="dd-empty">carregando…</span></span></>
          )}
        </div>
      </section>

      <section className="dd-sec">
        <div className="dd-sec-head">
          <h3 className="dd-sec-title"><LuSparkles size={13} /> Resumo IA</h3>
          {summaryNote && (
            <button type="button" className="dd-link" onClick={handleGenerateSummary} disabled={isGeneratingSummary}>
              {isGeneratingSummary ? 'Gerando…' : 'Regerar'}
            </button>
          )}
        </div>
        {summaryNote ? (
          <div className="dd-summary">{renderFormattedSummary(summaryNote.content)}</div>
        ) : (
          <div className="dd-summary-empty">
            <p>{criadaManual
              ? 'Oportunidade criada manualmente: o resumo é feito a partir das mensagens da conversa, e só fica disponível quando houver mensagens.'
              : 'Nenhum resumo gerado para esta conversa.'}</p>
            <button type="button" className="lk-btn" onClick={handleGenerateSummary} disabled={isGeneratingSummary}>
              <LuSparkles size={14} /> {isGeneratingSummary ? 'Gerando…' : 'Gerar resumo'}
            </button>
          </div>
        )}
      </section>
    </aside>
  );

  return (
    <div className="page-shell dd-screen pl-screen lk-screen">
      <PageHeader
        leading={voltar}
        title={deal.title}
        subtitle={subtitulo}
        actions={
          <div className="lk-actions">
            {/* Etapa e temperatura lado a lado: as duas decisões que o vendedor toma sobre o
                negócio sem abrir nada. A etapa vem primeiro porque é a que move o funil.
                Mudar aqui passa pelo mesmo caminho da régua (`handleStatusChange`), então
                Perdido continua pedindo o motivo e Ganho continua exigindo o produto. */}
            {!deal.foraDoFunil && (
              <Select size="control" block={false} value={String(statusId)} options={opcoesEtapa} onChange={handleStatusChange}
                disabled={isUpdatingStatus} showOptionAccessory ariaLabel="Etapa" title="Etapa da oportunidade" />
            )}
            {/* Contato fora do funil não tem temperatura (igual ao cabeçalho do Chat). */}
            {!deal.foraDoFunil && (
              <Select size="control" block={false} value={String(tipoLeadId ?? 0)} options={TEMPERATURAS} onChange={handleLeadTypeChange}
                disabled={isUpdatingLeadType} showOptionAccessory ariaLabel="Temperatura" title="Temperatura do lead" />
            )}
            {((semDono && currentUser) || adminPodeTomar) && (
              <button type="button" className="lk-btn" onClick={() => (semDono ? handleAssumeDeal() : setConfirmandoTomar(true))} disabled={isAssuming}>
                <LuUserPlus size={14} /> {isAssuming ? 'Assumindo…' : 'Assumir'}
              </button>
            )}
            {/* Tirar do funil saiu de dentro do dialogo de excluir e virou botao proprio, a esquerda
                de Excluir: um e reversivel (o contato volta pela faixa), o outro nao. */}
            {!!deal.contactId && !deal.foraDoFunil && statusId !== 3 && (
              <button type="button" className="lk-btn" onClick={() => setConfirmandoForaDoFunil(true)} disabled={mudandoFunil}>
                <LuFilterX size={14} /> Tirar do funil
              </button>
            )}
            {/* Excluir e so do administrador da empresa — papel (`tipoUsuarioCodigo`), nao permissao
                de catalogo. A API recusa de qualquer jeito; o botao some em vez de dar erro. */}
            {podeExcluir && (
              <button type="button" className="lk-btn dd-danger" onClick={() => setIsDeleteConfirmOpen(true)}>
                <LuTrash2 size={14} /> Excluir
              </button>
            )}
          </div>
        }
        band={deal.foraDoFunil ? faixaForaDoFunil : reguaEtapas}
      />

      <div className="page-body dd-body">
        <div className="dd-layout">
          {/* ─── Ficha ─── No celular ela é a aba Dados, dentro do painel (ver `ficha`). */}
          {!celular && ficha}

          {/* ─── Conteúdo da aba ─── */}
          <section className="dd-pane">
            {tiraAbas(acaoAba)}
            {celular && activeTab === 'dados' && <div className="dd-pane-body dd-dados">{ficha}</div>}
            {activeTab === 'agenda' && (
              <>
                <div className="dd-pane-body">
                  {/* Próximo passo: a tarefa padrão aberta desta etapa. Registrar o desfecho é
                      o que move o funil (docs/propostas/tarefas-padrao.html, tela 2). */}
                  {!isLoadingTasks && proximoPasso && (() => {
                    const venc = proximoPasso.dataRetorno ? new Date(proximoPasso.dataRetorno) : null;
                    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
                    const fimHoje = new Date(hoje); fimHoje.setDate(fimHoje.getDate() + 1);
                    const vencida = !!venc && venc < hoje;
                    const venceHoje = !!venc && !vencida && venc < fimHoje;
                    const tipo = tiposTarefa.find(t => t.tipoTarefaId === proximoPasso.tipoTarefaId);
                    const responsavel = usuariosEmpresa.find(u => u.id === proximoPasso.responsavelId)?.nome
                      ?? (proximoPasso.responsavelId && currentUser?.id === proximoPasso.responsavelId ? currentUser.nome : null);
                    return (
                      <section className="dd-np" aria-label="Próximo passo">
                        <div className="dd-np-top">
                          <span className="dd-np-title">{proximoPasso.descricao}</span>
                          <span className="dd-np-meta">
                            próximo passo
                            {venc && <> · vence <b className={vencida ? 'is-late' : venceHoje ? 'is-hoje' : ''}>{vencida ? `${dataHora(proximoPasso.dataRetorno!).split(',')[0]} · vencida` : venceHoje ? 'hoje' : dataHora(proximoPasso.dataRetorno!).split(',')[0]}</b></>}
                            {proximoPasso.tentativas > 0 && <> · {proximoPasso.tentativas + 1}ª tentativa</>}
                          </span>
                        </div>
                        <div className="dd-np-sub">
                          Criada ao entrar em {etapaAtual?.label ?? 'esta etapa'}
                          {responsavel ? ` · responsável ${responsavel}` : ' · sem responsável'}
                          {tipo ? ` · tipo ${tipo.nome}` : ''}
                        </div>
                        <div className="dd-np-row">
                          <Select
                            value={desfechoEscolhido}
                            onChange={setDesfechoEscolhido}
                            placeholder="O que aconteceu?"
                            disabled={registrandoDesfecho}
                            options={proximoPasso.desfechos.map(d => ({
                              value: String(d.desfechoId),
                              label: d.rotulo,
                              meta: descreverAcao(d.acao, d.statusDestinoId, d.diasReagendamento),
                            }))}
                          />
                          <button type="button" className="lk-btn is-primary" disabled={!desfechoEscolhido || registrandoDesfecho} onClick={confirmarDesfecho}>
                            {registrandoDesfecho ? 'Registrando…' : 'Registrar'}
                          </button>
                        </div>
                      </section>
                    );
                  })()}
                  {!isLoadingTasks && proximoPasso && tasks.length > 1 && <div className="dd-np-lbl">Outras tarefas</div>}
                  {isLoadingTasks ? (
                    [1, 2, 3].map(i => <div key={i} className="pl-skel" style={{ height: 52, marginBottom: 8 }} />)
                  ) : tasks.filter(t => t !== proximoPasso).length > 0 ? (
                    <div className="dd-list">
                      {tasks.filter(t => t !== proximoPasso).map(task => {
                        const tipo = tiposTarefa.find(t => t.tipoTarefaId === task.tipoTarefaId);
                        const superada = task.estado === 'superseded';
                        const padrao = task.origem === 'default';
                        const concluida = task.estaConcluida || task.estado === 'done';
                        const atrasada = !concluida && !superada && task.dataRetorno && new Date(task.dataRetorno) < new Date();
                        const responsavel = usuariosEmpresa.find(u => u.id === task.responsavelId)?.nome;
                        return (
                          <div key={task.tarefaId} className={`dd-task ${concluida ? 'is-done' : ''} ${superada ? 'is-superada' : ''}`}>
                            {superada
                              ? <span className="dd-check is-superada" aria-hidden="true" />
                              : (
                                <button type="button" className={`dd-check ${concluida ? 'is-on' : ''}`}
                                  aria-label={concluida ? 'Reabrir tarefa' : 'Concluir tarefa'} onClick={() => handleToggleTask(task)}
                                  disabled={padrao && concluida}>
                                  {concluida && <LuCheck size={12} />}
                                </button>
                              )}
                            <div className="dd-task-text">
                              <div className="dd-task-title">{task.descricao}</div>
                              <div className="dd-task-meta">
                                {padrao && <span className="lk-tag">padrão</span>}
                                {tipo && !padrao && <span className="lk-tag">{tipo.nome}</span>}
                                {superada && <span>superada quando a etapa mudou</span>}
                                {padrao && concluida && task.desfechoRotulo && <span>desfecho: {task.desfechoRotulo}</span>}
                                {!superada && task.dataRetorno && <span className={atrasada ? 'dd-late' : ''}>Retorno {dataHora(task.dataRetorno)}{atrasada ? ' · atrasada' : ''}</span>}
                                {responsavel && <span>{responsavel}</span>}
                              </div>
                            </div>
                            {!padrao && (
                              <div className="dd-row-acts">
                                <button type="button" className="lk-act" aria-label="Editar tarefa" title="Editar tarefa" onClick={() => openEditTaskModal(task)}><LuPencil size={14} /></button>
                                <button type="button" className="lk-act is-danger" aria-label="Excluir tarefa" title="Excluir tarefa" onClick={() => handleDeleteTask(task.tarefaId)}><LuTrash2 size={14} /></button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : proximoPasso ? null : (
                    /* Sem botão no vazio (ele, 2026-09-23): a ação já está no canto da
                       linha das abas, e repeti-la aqui punha dois botões iguais na mesma
                       tela. O ícone ocupa o lugar dela e diz de que seção é o vazio. */
                    <div className="dd-blank">
                      <LuListTodo className="dd-blank-icon" size={26} aria-hidden="true" />
                      <div className="dd-blank-title">Nenhuma tarefa</div>
                      <p className="dd-blank-text">Crie tarefas para não perder prazos e acompanhar a evolução do negócio.</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'notes' && (
              <>
                <div className="dd-pane-body">
                  {showAddDetails && (
                    formularioAnotacao('Nova anotação')
                  )}
                  {isLoadingNotes ? (
                    [1, 2, 3].map(i => <div key={i} className="pl-skel" style={{ height: 72, marginBottom: 8 }} />)
                  ) : filteredNotes.length > 0 ? (
                    <div className="dd-list">
                      {filteredNotes.map(obs => {
                        if (editingNoteId === obs.id) return <div key={obs.id}>{formularioAnotacao('Editando anotação')}</div>;
                        // A API recusa mexer em anotação alheia ou do sistema; a tela nem oferece.
                        const doSistema = /^(Resumo gerado pelo sistema:|Resumo da Conversa:|Alteração de valor:)/i.test(obs.content ?? '');
                        const minha = currentUser !== null && obs.userId === currentUser.id && !doSistema;
                        const anexosDaNota = attachments.filter(a => a.observacaoId === obs.id);
                        return (
                          <article key={obs.id} className="dd-note">
                            <div className="dd-note-head">
                              <strong>{obs.userName}</strong>
                              <span className="dd-note-right">
                                <span>{dataHora(obs.createdAt)}</span>
                                {minha && (
                                  <span className="dd-row-acts">
                                    <button type="button" className="lk-act" aria-label="Editar anotação" title="Editar anotação" onClick={() => abrirEdicaoAnotacao(obs)}><LuPencil size={13} /></button>
                                    <button type="button" className="lk-act is-danger" aria-label="Excluir anotação" title="Excluir anotação" onClick={() => setNoteToDelete(obs)}><LuTrash2 size={13} /></button>
                                  </span>
                                )}
                              </span>
                            </div>
                            {ehHtml(obs.content)
                              ? <div className="dd-note-body oe-doc is-view" dangerouslySetInnerHTML={{ __html: obs.content }} />
                              : <p className="dd-note-body">{obs.content}</p>}
                            {anexosDaNota.length > 0 && (
                              <div className="dd-chips">
                                {anexosDaNota.map(a => (
                                  <span key={a.id} className="dd-chip">
                                    {iconeAnexo(a.tipoAnexo)}
                                    <span className="dd-chip-name" title={nomeArquivo(a.caminhoAnexo)}>{nomeArquivo(a.caminhoAnexo)}</span>
                                    <button type="button" className="dd-chip-act" aria-label="Ver" title="Ver" onClick={() => handleViewAttachment(a.id)}><LuEye size={12} /></button>
                                    <button type="button" className="dd-chip-act" aria-label="Baixar" title="Baixar" onClick={() => handleDownloadAttachment(a.id, a.caminhoAnexo || '')}><LuDownload size={12} /></button>
                                    {minha && <button type="button" className="dd-chip-act is-danger" aria-label="Remover anexo" title="Remover anexo" onClick={() => handleRemoveAttachment(a.id)}><LuX size={12} /></button>}
                                  </span>
                                ))}
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  ) : !showAddDetails && (
                    <div className="dd-blank">
                      <LuStickyNote className="dd-blank-icon" size={26} aria-hidden="true" />
                      <div className="dd-blank-title">Nenhuma anotação</div>
                      <p className="dd-blank-text">Registre aqui o histórico e os detalhes da negociação. Dá para anexar arquivos: clique, arraste ou cole.</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'chat' && (
              <div className="dd-chat ch-screen">
                {isLoadingChat ? (
                  <ConversationSkeleton />
                ) : chatError ? (
                  <div className="dd-blank"><div className="dd-blank-title">Não deu para carregar a conversa</div><p className="dd-blank-text">{chatError}</p></div>
                ) : (
                  <>
                    <ConversationWindow messagesByDate={messagesByDate} conversationId={deal.id} atribuicaoAnuncio={deal.atribuicaoAnuncio} />
                    <MessageInput
                      onSendMessage={handleSendMessage}
                      disabled={chatBloqueado || janelaFechada !== null}
                      destinatario={deal.title}
                      focoKey={deal.id}
                      disabledMessage={
                        !chatBloqueado
                          ? janelaFechada ?? undefined
                          : currentUser !== null && currentUser.nome !== deal.owner
                            ? 'Só o responsável envia mensagens nesta conversa'
                            : 'Nesta etapa o chat é só leitura'
                      }
                    />
                  </>
                )}
              </div>
            )}

            {activeTab === 'attachments' && (
              <>
                <div className="dd-pane-body">
                  {isLoadingAttachments ? (
                    [1, 2, 3].map(i => <div key={i} className="pl-skel" style={{ height: 52, marginBottom: 8 }} />)
                  ) : attachments.length > 0 ? (
                    <div className="dd-list">
                      {attachments.map(att => (
                        <div key={att.id} className="dd-file">
                          <AttachmentPreview id={att.id} tipo={att.tipoAnexo} />
                          <span className="dd-file-name" title={nomeArquivo(att.caminhoAnexo)}>{nomeArquivo(att.caminhoAnexo)}</span>
                          <div className="dd-row-acts">
                            <button type="button" className="lk-act" aria-label="Ver" title="Ver" onClick={() => handleViewAttachment(att.id)}><LuEye size={14} /></button>
                            <button type="button" className="lk-act" aria-label="Baixar" title="Baixar" onClick={() => handleDownloadAttachment(att.id, att.caminhoAnexo || '')}><LuDownload size={14} /></button>
                            <button type="button" className="lk-act is-danger" aria-label="Excluir anexo" title="Excluir anexo" onClick={() => handleRemoveAttachment(att.id)}><LuTrash2 size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="dd-blank">
                      <LuPaperclip className="dd-blank-icon" size={26} aria-hidden="true" />
                      <div className="dd-blank-title">Nenhum anexo</div>
                      <p className="dd-blank-text">Documentos, imagens e mídias trocados nesta oportunidade ficam aqui.</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'products' && (
              <>
                <div className="dd-pane-body">
                  {isLoadingDealProducts ? (
                    [1, 2].map(i => <div key={i} className="pl-skel" style={{ height: 40, marginBottom: 8 }} />)
                  ) : dealProducts.length > 0 ? (
                    <div className="lk-table dd-products">
                      <div className="lk-row" style={{ gridTemplateColumns: DD_PRODUTOS }}>
                        <div className="lk-head">Código</div><div className="lk-head">Produto</div><div className="lk-head">Marca</div>
                        <div className="lk-head">Qtd</div><div className="lk-head">Unitário</div><div className="lk-head">Total</div><div className="lk-head lk-head-acts">Ações</div>
                      </div>
                      <div className="lk-body">
                        {dealProducts.map(p => (
                          <div key={p.oportunidadeProdutoId} className="lk-row" style={{ gridTemplateColumns: DD_PRODUTOS }}>
                            <span className="lk-cell lk-muted"><span>{p.codigo ?? '—'}</span></span>
                            <span className="lk-cell lk-strong"><span>{p.nome ?? '—'}</span></span>
                            <span className="lk-cell lk-muted"><span>{p.marca || '—'}</span></span>
                            <span className="lk-cell">
                              <span className="dd-qty">
                                <button type="button" aria-label="Diminuir quantidade" onClick={() => alterarQuantidade(p, Math.max(1, p.quantidade - 1))}><LuMinus size={12} /></button>
                                <span>{p.quantidade}</span>
                                <button type="button" aria-label="Aumentar quantidade" onClick={() => alterarQuantidade(p, p.quantidade + 1)}><LuPlus size={12} /></button>
                              </span>
                            </span>
                            <span className="lk-cell"><span className="pl-card-val">{brl(p.precoUnitarioNegociado)}</span></span>
                            <span className="lk-cell"><span className="pl-card-val">{brl(p.precoTotal)}</span></span>
                            <span className="lk-cell lk-acts-cell">
                              <button type="button" className="lk-act is-danger" aria-label="Remover produto" title="Remover produto" onClick={() => removerProduto(p)}><LuTrash2 size={14} /></button>
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="lk-foot dd-total">
                        <span>Total dos produtos</span>
                        <strong>{brl(dealProducts.reduce((sum, p) => sum + p.precoTotal, 0))}</strong>
                      </div>
                    </div>
                  ) : (
                    <div className="dd-blank">
                      <LuPackage className="dd-blank-icon" size={26} aria-hidden="true" />
                      <div className="dd-blank-title">Nenhum produto vinculado</div>
                      <p className="dd-blank-text">Vincule produtos ou serviços para calcular o valor da oportunidade. Ganhar exige pelo menos um.</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'documents' && (
              <DocumentosOportunidade
                conversaId={deal.id}
                emitirAberto={emitirDocAberto}
                onFecharEmitir={() => setEmitirDocAberto(false)}
              />
            )}

            {activeTab === 'assinatura' && (
              <AbaAssinatura
                conversationId={deal.id}
                nomeContato={deal.title}
                valorNegocio={deal.rawValue}
              />
            )}

            {activeTab === 'history' && (
              <div className="dd-pane-body">
                <SecaoHistorico entidade="oportunidade" id={deal.id} variante="pagina" />
              </div>
            )}

          </section>
        </div>
      </div>

      {/* ─── Tarefa: formulário em painel ─── */}
      <RecordPanel
        open={isTaskModalOpen}
        onClose={closeTaskModal}
        title={editingTask ? 'Editar tarefa' : 'Nova tarefa'}
        editing
        footer={
          <>
            <button type="button" className="rp-btn" onClick={closeTaskModal} disabled={isSavingTask}>Cancelar</button>
            <button type="button" className="rp-btn is-primary" onClick={handleModalSubmit} disabled={isSavingTask}>
              {isSavingTask ? 'Salvando…' : editingTask ? 'Salvar' : 'Criar tarefa'}
            </button>
          </>
        }
      >
        <PanelSection title="Tarefa">
          <div className="rp-field">
            <label className="rp-label" htmlFor="dd-tarefa-desc">Descrição *</label>
            <textarea id="dd-tarefa-desc" className="rp-input is-area" rows={3} value={modalDescricao} onChange={e => setModalDescricao(e.target.value)}
              placeholder="Ex.: ligar para apresentar a proposta" autoFocus />
          </div>
          <div className="rp-field">
            <label className="rp-label">Tipo *</label>
            <Select value={modalTipoTarefaId ? String(modalTipoTarefaId) : ''} onChange={v => setModalTipoTarefaId(Number(v))}
              placeholder={tiposTarefa.length ? 'Selecionar' : 'Carregando tipos…'} options={tiposTarefa.map(t => ({ value: String(t.tipoTarefaId), label: t.nome }))} />
          </div>
          <div className="rp-field">
            <label className="rp-label">Responsável</label>
            <Select value={modalResponsavelId ? String(modalResponsavelId) : ''} onChange={v => setModalResponsavelId(v ? Number(v) : null)}
              placeholder="Quem criou a tarefa"
              options={(usuariosEmpresa.length > 0 ? usuariosEmpresa : (currentUser ? [currentUser] : [])).map(u => ({ value: String(u.id), label: u.nome }))} />
          </div>
        </PanelSection>
        <PanelSection title="Retorno">
          <div className="rp-two">
            <div className="rp-field">
              <label className="rp-label" htmlFor="dd-tarefa-data">Data</label>
              <input id="dd-tarefa-data" type="date" className="rp-input" value={paraDataInput(modalDate)}
                onChange={e => setModalDate(e.target.value ? new Date(`${e.target.value}T00:00:00`) : null)} />
            </div>
            <div className="rp-field">
              <label className="rp-label" htmlFor="dd-tarefa-hora">Hora</label>
              <input id="dd-tarefa-hora" type="time" className="rp-input" value={modalTime} onChange={e => setModalTime(e.target.value)} />
            </div>
          </div>
          <div className="dd-shortcuts">
            {[1, 2, 6, 12, 24].map(h => (
              <button key={h} type="button" className="rp-btn" onClick={() => handleReturnShortcut(h)}>Daqui {h}h</button>
            ))}
          </div>
        </PanelSection>
      </RecordPanel>

      {/* ─── Produto: formulário em painel ─── */}
      <RecordPanel
        open={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        title="Vincular produto"
        editing
        footer={
          <>
            <button type="button" className="rp-btn" onClick={() => setIsProductModalOpen(false)}>Cancelar</button>
            <button type="button" className="rp-btn is-primary" disabled={!selectedProductId} onClick={vincularProduto}>Vincular</button>
          </>
        }
      >
        <PanelSection title="Produto">
          <div className="rp-field">
            <Select
              value={selectedProductId ? String(selectedProductId) : ''}
              onChange={v => { const p = availableProducts.find(x => String(x.produtoId) === v); setSelectedProductId(Number(v)); if (p) setProductCustomPrice(p.preco.toString()); }}
              placeholder={isLoadingProducts ? 'Carregando produtos…' : 'Buscar por nome, marca ou código…'}
              searchFrom={1}
              emptyText={availableProducts.length === 0 ? 'Nenhum produto cadastrado' : 'Nenhum produto com esse nome'}
              options={produtosFiltrados.map(p => ({ value: String(p.produtoId), label: p.nome, meta: `${p.codigo}${p.marca ? ` · ${p.marca}` : ''} · ${brl(p.preco)}` }))}
            />
          </div>
          {selectedProductId && (
            <div className="rp-two">
              <div className="rp-field">
                <label className="rp-label" htmlFor="dd-prod-qtd">Quantidade</label>
                <input id="dd-prod-qtd" type="number" min={1} className="rp-input" value={productQuantity}
                  onChange={e => setProductQuantity(Math.max(1, parseInt(e.target.value) || 1))} />
              </div>
              <div className="rp-field">
                <label className="rp-label" htmlFor="dd-prod-preco">Preço unitário negociado</label>
                <input id="dd-prod-preco" type="number" step="0.01" min={0} className="rp-input" value={productCustomPrice}
                  onChange={e => setProductCustomPrice(e.target.value)} placeholder="Preço de tabela" />
              </div>
            </div>
          )}
        </PanelSection>
      </RecordPanel>

      <Dialog
        open={confirmandoTomar}
        onClose={() => setConfirmandoTomar(false)}
        title={`Assumir a oportunidade «${deal.title}»?`}
        confirmLabel="Assumir"
        busy={isAssuming}
        onConfirm={handleAssumeDeal}
      >
        Ela sai de {deal.owner} e passa a ser sua.
      </Dialog>

      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        title={`Excluir a oportunidade de "${deal.title}"?`}
        confirmLabel="Excluir"
        cancelLabel="Manter"
        danger
        busy={isDeleting}
        onConfirm={handleDelete}
      >
        Excluir apaga a conversa e todas as mensagens dela, e não tem volta. O contato continua existindo.
      </Dialog>

      {/* O caminho reversivel, agora com dialogo proprio em vez de botao dentro do de excluir. */}
      <Dialog
        open={confirmandoForaDoFunil}
        onClose={() => setConfirmandoForaDoFunil(false)}
        title={`Tirar «${deal.title}» do funil?`}
        confirmLabel="Tirar do funil"
        cancelLabel="Cancelar"
        busy={mudandoFunil}
        onConfirm={() => { setConfirmandoForaDoFunil(false); alterarFunil(true); }}
      >
        O contato sai da Pipeline e das métricas. As conversas continuam no Chat, e dá para trazer de volta por aqui.
      </Dialog>

      <Dialog
        open={noteToDelete !== null}
        onClose={() => setNoteToDelete(null)}
        title="Excluir esta anotação?"
        confirmLabel="Excluir"
        cancelLabel="Manter"
        danger
        busy={isDeletingNote}
        onConfirm={excluirAnotacao}
      >
        Os arquivos anexados a ela continuam na aba Anexos da oportunidade.
      </Dialog>

      <LossReasonModal isOpen={isLossModalOpen} dealTitle={deal.title} onConfirm={handleLossReasonConfirm} onCancel={handleLossReasonCancel} />
      <WinDealProductModal isOpen={isWinProductModalOpen} dealId={deal.id} dealTitle={deal.title} onConfirm={handleWinProductConfirm} onCancel={handleWinProductCancel} />
    </div>
  );
};

export default DealDetailsPage;
